// Content-hub registry — the single source of truth for nReport's editorial
// guides ("vodnik" / "guide"). Plain ESM + JSDoc so it can be imported by BOTH:
//   • the Node build layer (scripts/seo-config.js) for prerender routes, SEO
//     meta, JSON-LD (Article + FAQPage + BreadcrumbList) and the sitemap, and
//   • the React app (App.tsx, GuidePageLayout, i18n/routes.ts) via allowJs.
//
// GEO rationale: generative engines cite pages that answer a concrete question
// with structured, sourced facts. Each guide leads with a direct answer, uses
// question-shaped H2s, cites official Slovenian sources, and carries an on-page
// FAQ that is mirrored 1:1 into FAQPage structured data. See
// docs/geo-audit-and-plan.md (Phase 2).
//
// To add a locale to a guide, add a key under `locales`. hreflang, sitemap and
// prerender routes are derived automatically from whatever locales are present.

/**
 * @typedef {{ label: string, href: string }} SourceLink
 * @typedef {(
 *   | { p: string }
 *   | { h3: string }
 *   | { ul: string[] }
 *   | { ol: string[] }
 *   | { steps: { title: string, text: string }[] }
 *   | { table: { head: string[], rows: string[][] } }
 *   | { callout: { title?: string, text: string } }
 *   | { sources: SourceLink[] }
 *   | { terms: GuideTerm[] }
 * )} GuideBlock
 * @typedef {{ term: string, alternateName?: string, definition: string }} GuideTerm
 * @typedef {{ question: string, answer: string }} GuideFaq
 * @typedef {{
 *   path: string, title: string, description: string, breadcrumb: string,
 *   eyebrow: string, h1: string, intro: string,
 *   sections: { h2: string, blocks: GuideBlock[] }[], faq: GuideFaq[]
 * }} GuideLocale
 * @typedef {{
 *   group: string, datePublished: string, dateModified: string,
 *   changefreq: string, priority: string,
 *   locales: Partial<Record<'sl'|'en'|'de'|'ru', GuideLocale>>
 * }} Guide
 */

const SITE_URL = 'https://www.nreport.ai';
const PUBLISHED = '2026-07-26';

/** Localized labels for hub/breadcrumb/FAQ chrome, per locale. */
export const GUIDE_UI = {
  sl: {
    home: 'Domov',
    hubBreadcrumb: 'Vodnik',
    updated: 'Posodobljeno',
    onThisPage: 'Na tej strani',
    faqTitle: 'Pogosta vprašanja',
    related: 'Sorodni vodniki',
    sources: 'Viri',
    ctaTitle: 'Pripravite poročilo o nepremičnini',
    ctaText:
      'Uradni podatki GURS in ETN, zemljiškoknjižno stanje in AI ocena vrednosti — v enem PDF dokumentu. Predogled poročila je brezplačen.',
    ctaButton: 'Ustvarite poročilo',
    disclaimer:
      'Vsebina je informativne narave in ne nadomešča uradne cenitve pooblaščenega ocenjevalca vrednosti nepremičnin.',
  },
  en: {
    home: 'Home',
    hubBreadcrumb: 'Guide',
    updated: 'Updated',
    onThisPage: 'On this page',
    faqTitle: 'Frequently asked questions',
    related: 'Related guides',
    sources: 'Sources',
    ctaTitle: 'Get a property report',
    ctaText:
      'Official GURS and ETN data, land-registry status and an AI value estimate — in one PDF. The report preview is free.',
    ctaButton: 'Create a report',
    disclaimer:
      'This content is informational and does not replace an official appraisal by a licensed real-estate valuer.',
  },
};

/** @type {{ group: 'hub', locales: Record<'sl'|'en', { path: string, title: string, description: string, eyebrow: string, h1: string, intro: string }> }} */
export const GUIDE_HUB = {
  group: 'hub',
  locales: {
    sl: {
      path: '/vodnik',
      title: 'Vodnik po nepremičninah v Sloveniji | nReport',
      description:
        'Razumljivi vodniki o vrednosti nepremičnin, GURS posplošeni vrednosti, evidenci trga nepremičnin (ETN) in preverjanju nepremičnine pred nakupom. Podprto z uradnimi viri.',
      eyebrow: 'Vodnik',
      h1: 'Vodnik po nepremičninah v Sloveniji',
      intro:
        'Praktični, na uradnih podatkih temelječi vodniki, ki pojasnijo, kako se v Sloveniji določa in preverja vrednost nepremičnine — od GURS posplošene vrednosti in evidence realiziranih poslov (ETN) do zemljiške knjige in pregleda pred nakupom.',
    },
    en: {
      path: '/en/guide',
      title: 'Slovenia Property Guide | nReport',
      description:
        'Plain-language guides to property value in Slovenia, the GURS generalized market value, the ETN transaction register, and how to check a property before buying. Backed by official sources.',
      eyebrow: 'Guide',
      h1: 'The Slovenia Property Guide',
      intro:
        'Practical, data-backed guides that explain how property value is determined and verified in Slovenia — from the GURS generalized value and the register of realized sales (ETN) to the land register and pre-purchase due diligence.',
    },
  },
};

/** @type {Guide[]} */
export const GUIDES = [
  // ---------------------------------------------------------------- valuation
  {
    group: 'valuation',
    datePublished: PUBLISHED,
    dateModified: PUBLISHED,
    changefreq: 'monthly',
    priority: '0.8',
    locales: {
      sl: {
        path: '/vodnik/kako-oceniti-vrednost-nepremicnine',
        title: 'Kako oceniti vrednost nepremičnine v Sloveniji (2026) | nReport',
        description:
          'Kako oceniti vrednost stanovanja ali hiše v Sloveniji: GURS posplošena vrednost, realizirane cene iz ETN, primerjava z oglasi in kdaj potrebujete uradno cenitev.',
        breadcrumb: 'Kako oceniti vrednost nepremičnine',
        eyebrow: 'Vrednotenje',
        h1: 'Kako oceniti vrednost nepremičnine v Sloveniji',
        intro:
          'Vrednost nepremičnine v Sloveniji najbolj zanesljivo ocenite tako, da združite tri vire, ki merijo različne stvari: GURS posplošeno vrednost (modelirano vrednost iz množičnega vrednotenja), realizirane prodajne cene primerljivih nepremičnin iz evidence trga nepremičnin (ETN) in trenutne oglaševane cene v okolici. Nobena posamezna številka ni popolna — šele njihova kombinacija pokaže realen razpon.',
        sections: [
          {
            h2: 'Kateri viri podatkov določajo vrednost?',
            blocks: [
              {
                p: 'V Sloveniji so javno dostopni trije ključni viri, ki jih vodi Geodetska uprava Republike Slovenije (GURS) oz. sodišča. Vsak pove nekaj drugega:',
              },
              {
                table: {
                  head: ['Vir', 'Kaj pove', 'Omejitev'],
                  rows: [
                    ['GURS posplošena vrednost', 'Modelirana vrednost vsake evidentirane nepremičnine', 'Množični model, ne upošteva stanja konkretne enote'],
                    ['ETN (evidenca trga nepremičnin)', 'Dejansko realizirane prodajne in najemne cene', 'Časovni zamik; malo poslov za redke tipe'],
                    ['Oglaševane cene', 'Trenutna pričakovanja prodajalcev', 'Oglaševana cena je pogosto višja od realizirane'],
                    ['Zemljiška knjiga', 'Lastništvo in bremena (hipoteke, služnosti)', 'Ne pove vrednosti, a vpliva nanjo'],
                  ],
                },
              },
            ],
          },
          {
            h2: 'Kako po korakih oceniti vrednost',
            blocks: [
              {
                steps: [
                  { title: 'Pridobite GURS posplošeno vrednost', text: 'Poiščite posplošeno vrednost nepremičnine prek portala Prostor (GURS). To je izhodiščna, uradna referenčna vrednost.' },
                  { title: 'Preverite realizirane cene v ETN', text: 'Poglejte, po kakšnih cenah na m² so se v zadnjih 12–24 mesecih prodale primerljive nepremičnine v isti soseski ali naselju.' },
                  { title: 'Primerjajte z aktualnimi oglasi', text: 'Oglaševane cene pokažejo zgornjo mejo pričakovanj; upoštevajte razliko med oglaševano in realizirano ceno.' },
                  { title: 'Prilagodite konkretni enoti', text: 'Popravite navzgor ali navzdol glede na nadstropje, orientacijo, stanje, leto prenove, energetski razred in lego.' },
                  { title: 'Določite razpon, ne ene številke', text: 'Rezultat naj bo realen razpon (npr. ±5–10 %), znotraj katerega je tržna vrednost najverjetnejša.' },
                ],
              },
            ],
          },
          {
            h2: 'Zakaj se GURS vrednost pogosto razlikuje od tržne cene?',
            blocks: [
              {
                p: 'GURS posplošena vrednost je rezultat množičnega vrednotenja — statističnega modela, ki oceni vse nepremičnine hkrati na podlagi lokacije, velikosti in tipa. Ne pozna konkretnega stanja vaše enote (prenova, pogled, hrup, vlaga). Zato je koristna kot izhodišče in referenca, ne pa kot dokončna tržna cena.',
              },
              {
                callout: {
                  title: 'Pravilo palca',
                  text: 'Realizirane ETN cene uporabite kot dokaz trga, oglase kot indikator trenutnih pričakovanj prodajalcev, GURS vrednost pa kot referenco. GURS model temelji tudi na podatkih o realiziranih poslih, zato njegovo ujemanje z ETN ni povsem neodvisna potrditev.',
                },
              },
            ],
          },
          {
            h2: 'Kdaj potrebujete uradno cenitev?',
            blocks: [
              {
                p: 'Informativna ocena (tudi AI ocena) zadošča za lastno odločanje, pogajanje ali okvirno orientacijo. Za pravno zavezujoče namene pa potrebujete cenitev pooblaščenega ocenjevalca vrednosti nepremičnin ali sodnega cenilca:',
              },
              {
                ul: [
                  'hipotekarni kredit oz. zavarovanje pri banki,',
                  'zapuščinski, ločitveni ali sodni postopki,',
                  'delitev solastnine in poslovne transakcije z računovodskim učinkom.',
                ],
              },
            ],
          },
          {
            h2: 'Viri in uradne evidence',
            blocks: [
              {
                sources: [
                  { label: 'GURS — Geodetska uprava RS', href: 'https://www.gov.si/drzavni-organi/organi-v-sestavi/geodetska-uprava/' },
                  { label: 'Portal Prostor (javni vpogled v podatke o nepremičninah)', href: 'https://www.e-prostor.gov.si/' },
                  { label: 'Zemljiška knjiga — e-Sodstvo (javni vpogled)', href: 'https://esodisce.si/' },
                ],
              },
            ],
          },
        ],
        faq: [
          {
            question: 'Ali lahko vrednost nepremičnine ocenim brezplačno?',
            answer:
              'Delno. GURS posplošeno vrednost in del podatkov ETN lahko brezplačno pogledate prek portala Prostor. Združen pregled vseh virov z AI oceno v enem PDF poročilu je pri nReportu plačljiv.',
          },
          {
            question: 'Je GURS posplošena vrednost enaka tržni vrednosti?',
            answer:
              'Ne nujno. Posplošena vrednost je modelirana in ne upošteva stanja konkretne enote, zato lahko odstopa od dejanske tržne cene navzgor ali navzdol. Uporabna je kot izhodišče, ne kot dokončna cena.',
          },
          {
            question: 'Kateri vir je najbolj zanesljiv za tržno ceno?',
            answer:
              'Realizirane cene iz evidence trga nepremičnin (ETN), ker temeljijo na dejansko sklenjenih poslih. Oglaševane cene so praviloma višje od realiziranih.',
          },
        ],
      },
      en: {
        path: '/en/guide/how-to-value-property-slovenia',
        title: 'How to Value a Property in Slovenia (2026) | nReport',
        description:
          'How to estimate the value of an apartment or house in Slovenia: the GURS generalized value, realized ETN prices, comparison with listings, and when you need an official appraisal.',
        breadcrumb: 'How to value a property',
        eyebrow: 'Valuation',
        h1: 'How to Value a Property in Slovenia',
        intro:
          'The most reliable way to estimate a property’s value in Slovenia is to combine three sources that measure different things: the GURS generalized value (a modelled value from mass valuation), realized sale prices of comparable properties from the real estate transaction register (ETN), and current listing prices nearby. No single figure is complete — only their combination reveals a realistic range.',
        sections: [
          {
            h2: 'Which data sources determine value?',
            blocks: [
              {
                p: 'Three public sources — maintained by the Surveying and Mapping Authority (GURS) and the courts — each tell you something different:',
              },
              {
                table: {
                  head: ['Source', 'What it tells you', 'Limitation'],
                  rows: [
                    ['GURS generalized value', 'A modeled value for every registered property', 'Mass model; ignores the condition of the specific unit'],
                    ['ETN (transaction register)', 'Actual realized sale and rental prices', 'Time lag; few deals for rare property types'],
                    ['Listing prices', 'Sellers’ current expectations', 'Asking price is usually higher than the realized price'],
                    ['Land register', 'Ownership and encumbrances (mortgages, easements)', 'Does not give a value but affects it'],
                  ],
                },
              },
            ],
          },
          {
            h2: 'How to estimate value, step by step',
            blocks: [
              {
                steps: [
                  { title: 'Get the GURS generalized value', text: 'Look up the property’s generalized value via the Prostor portal (GURS). This is your official reference baseline.' },
                  { title: 'Check realized prices in ETN', text: 'See what price per m² comparable properties in the same neighbourhood sold for over the last 12–24 months.' },
                  { title: 'Compare with current listings', text: 'Listings show the upper bound of expectations; account for the gap between asking and realized prices.' },
                  { title: 'Adjust for the specific unit', text: 'Correct up or down for floor, orientation, condition, renovation year, energy class and location.' },
                  { title: 'Produce a range, not one number', text: 'The output should be a realistic range (e.g. ±5–10%) within which market value most likely falls.' },
                ],
              },
            ],
          },
          {
            h2: 'Why does the GURS value often differ from the market price?',
            blocks: [
              {
                p: 'The GURS generalized value comes from mass appraisal — a statistical model that values all properties at once based on location, size and type. It does not know the specific state of your unit (renovation, view, noise, damp). So it is useful as a baseline and reference, not as a final market price.',
              },
              {
                callout: {
                  title: 'Rule of thumb',
                  text: 'Use realized ETN prices as market evidence, listings as a gauge of what sellers currently expect, and the GURS value as a reference. The GURS model is itself built partly on realized sales, so its agreement with ETN is not a fully independent confirmation.',
                },
              },
            ],
          },
          {
            h2: 'When do you need an official appraisal?',
            blocks: [
              {
                p: 'An informational estimate (including an AI estimate) is enough for your own decisions, negotiation or rough orientation. For legally binding purposes you need an appraisal by a licensed real-estate valuer or court-appointed appraiser:',
              },
              {
                ul: [
                  'a mortgage loan or bank collateral,',
                  'inheritance, divorce or court proceedings,',
                  'division of co-ownership and business transactions with accounting effect.',
                ],
              },
            ],
          },
          {
            h2: 'Sources and official registers',
            blocks: [
              {
                sources: [
                  { label: 'GURS — Surveying and Mapping Authority of Slovenia', href: 'https://www.gov.si/en/state-authorities/bodies-within-ministries/surveying-and-mapping-authority/' },
                  { label: 'Prostor portal (public property data)', href: 'https://www.e-prostor.gov.si/' },
                  { label: 'Land register — e-Justice (public access)', href: 'https://esodisce.si/' },
                ],
              },
            ],
          },
        ],
        faq: [
          {
            question: 'Can I estimate a property’s value for free?',
            answer:
              'Partly. The GURS generalized value and part of the ETN data can be viewed for free via the Prostor portal. A combined view of all sources with an AI estimate in a single PDF report is a paid nReport product.',
          },
          {
            question: 'Is the GURS generalized value the same as market value?',
            answer:
              'Not necessarily. The generalized value is modeled and ignores the condition of the specific unit, so it can deviate above or below the actual market price. It is useful as a baseline, not as a final price.',
          },
          {
            question: 'Which source is most reliable for market price?',
            answer:
              'Realized prices from the real estate transaction register (ETN), because they are based on actual concluded deals. Asking prices are typically higher than realized ones.',
          },
        ],
      },
    },
  },

  // --------------------------------------------------------------- gurs-value
  {
    group: 'gurs-value',
    datePublished: PUBLISHED,
    dateModified: '2026-09-29',
    changefreq: 'monthly',
    priority: '0.7',
    locales: {
      sl: {
        path: '/vodnik/gurs-posplosena-vrednost',
        title: 'GURS posplošena vrednost nepremičnine: kaj je in kako se izračuna | nReport',
        description:
          'Kaj je GURS posplošena tržna vrednost, kako nastane množično vrednotenje, čemu služi in zakaj se lahko razlikuje od tržne cene.',
        breadcrumb: 'GURS posplošena vrednost',
        eyebrow: 'Pojmi',
        h1: 'Kaj je GURS posplošena vrednost nepremičnine?',
        intro:
          'GURS posplošena vrednost je uradna, z modelom izračunana ocena vrednosti vsake evidentirane nepremičnine v Sloveniji. Določi jo Geodetska uprava RS s postopkom množičnega vrednotenja nepremičnin in služi kot referenca za javne namene, ne pa kot cenitev konkretne enote.',
        sections: [
          {
            h2: 'Kako nastane posplošena vrednost?',
            blocks: [
              {
                p: 'Posplošena vrednost je rezultat množičnega vrednotenja: GURS z modeli vrednotenja ovrednoti vse nepremičnine hkrati na podlagi njihovih evidentiranih lastnosti in podatkov o trgu iz evidence trga nepremičnin (ETN). Vsaki nepremičnini se pripiše vrednost glede na vrednostno cono in vrednostno raven za njen tip.',
              },
              {
                ul: [
                  'upošteva lokacijo (vrednostne cone), velikost, tip in leto,',
                  'temelji na statistiki realiziranih poslov, ne na ogledu enote,',
                  'se periodično indeksira in posodablja ob spremembah modelov.',
                ],
              },
            ],
          },
          {
            h2: 'Čemu služi posplošena vrednost?',
            blocks: [
              {
                p: 'Posplošena vrednost je namenjena javnim namenom. GURS navaja, da rezultati množičnega vrednotenja prispevajo k preglednosti trga nepremičnin in podpirajo socialne, prostorske in ekonomske politike države in občin. Uporablja se med drugim kot:',
              },
              {
                ul: [
                  'referenca v upravnih in statističnih postopkih,',
                  'okvirna referenca pri prometu z nepremičninami.',
                ],
              },
              {
                p: 'Pri davku na dediščine in darila davčna osnova za nepremičnino ni neposredno posplošena vrednost, temveč primerljiva tržna vrednost na dan nastanka davčne obveznosti. Če se zavezanec z ugotovljeno osnovo ne strinja, lahko v roku za pritožbo zahteva, da se upošteva ocenjena posamična tržna vrednost.',
              },
              {
                sources: [
                  { label: 'FURS — brošura o obdavčitvi nepremičnin', href: 'https://www.fu.gov.si/fileadmin/Internet/Davki_in_druge_dajatve/Podrocja/Davek_na_promet_nepremicnin/Opis/Brosura_o_obdavcitvi_nepremicnin.docx' },
                ],
              },
            ],
          },
          {
            h2: 'Zakaj se razlikuje od tržne cene?',
            blocks: [
              {
                p: 'Ker je modelirana, posplošena vrednost ne upošteva individualnega stanja: kakovosti prenove, pogleda, etaže, hrupa ali vlage. Poleg tega odraža raven cen na datum vrednotenja modela in ne sledi sprotnim spremembam trga. Zato lahko za konkretno enoto odstopa od dosežene tržne cene. Za tržno oceno jo vedno kombinirajte z realiziranimi cenami (ETN) in aktualnimi oglasi.',
              },
              {
                callout: {
                  text: 'Posplošena vrednost ni uradna cenitev. Za banko ali sodišče je potrebna cenitev pooblaščenega ocenjevalca.',
                },
              },
            ],
          },
          {
            h2: 'Kje preverim posplošeno vrednost?',
            blocks: [
              {
                p: 'Posplošeno vrednost svoje nepremičnine lahko preverite prek portala Prostor (GURS), kjer so na voljo javni podatki o nepremičninah.',
              },
              {
                sources: [
                  { label: 'GURS — o množičnem vrednotenju nepremičnin (e-prostor)', href: 'https://www.e-prostor.gov.si/podrocja/trg-in-vrednosti-nepremicnin/drugo/razno/o-mnozicnem-vrednotenju/' },
                  { label: 'Portal Prostor (javni vpogled)', href: 'https://www.e-prostor.gov.si/' },
                ],
              },
            ],
          },
        ],
        faq: [
          {
            question: 'Je posplošena vrednost enaka tržni vrednosti?',
            answer:
              'Ne. Posplošena vrednost je modelirana ocena za javne namene in ne upošteva stanja konkretne enote, zato lahko odstopa od dejanske tržne cene.',
          },
          {
            question: 'Kdo določi posplošeno vrednost?',
            answer:
              'Geodetska uprava Republike Slovenije (GURS) v okviru sistema množičnega vrednotenja nepremičnin.',
          },
          {
            question: 'Ali lahko posplošeno vrednost uporabim za bančno posojilo?',
            answer:
              'Ne kot uradno podlago. Banke zahtevajo cenitev pooblaščenega ocenjevalca vrednosti nepremičnin; posplošena vrednost je le referenca.',
          },
        ],
      },
      en: {
        path: '/en/guide/gurs-generalized-market-value',
        title: 'GURS Generalized Market Value: What It Is and How It’s Calculated | nReport',
        description:
          'What the GURS generalized market value is, how mass valuation produces it, what it is used for and why it can differ from the market price.',
        breadcrumb: 'GURS generalized value',
        eyebrow: 'Concepts',
        h1: 'What Is the GURS Generalized Market Value?',
        intro:
          'The GURS generalized value is an official, model-calculated estimate of the value of every registered property in Slovenia. It is produced by the Surveying and Mapping Authority (GURS) through mass real-estate valuation and serves as a reference for public purposes, not as an appraisal of a specific unit.',
        sections: [
          {
            h2: 'How is the generalized value produced?',
            blocks: [
              {
                p: 'The generalized value results from mass valuation: GURS values all properties at once using valuation models based on their registered characteristics and market data from the transaction register (ETN). Each property is assigned a value according to its value zone and value level for its type.',
              },
              {
                ul: [
                  'accounts for location (value zones), size, type and year,',
                  'is based on the statistics of realized deals, not an inspection of the unit,',
                  'is periodically indexed and updated when the models change.',
                ],
              },
            ],
          },
          {
            h2: 'What is the generalized value used for?',
            blocks: [
              {
                p: 'The generalized value is intended for public purposes. GURS says the results of mass valuation make the property market more transparent and support the social, spatial and economic policies of the state and municipalities. It is used, among other things, as:',
              },
              {
                ul: [
                  'a reference in administrative and statistical procedures,',
                  'a rough reference in property transactions.',
                ],
              },
              {
                p: 'For inheritance and gift tax, the tax base for a property is not the generalized value as such but its comparable market value on the day the tax liability arises. A taxpayer who disagrees with the assessed base can ask, within the appeal period, for an individual market valuation to be used instead.',
              },
              {
                sources: [
                  { label: 'FURS — brochure on property taxation (in Slovenian)', href: 'https://www.fu.gov.si/fileadmin/Internet/Davki_in_druge_dajatve/Podrocja/Davek_na_promet_nepremicnin/Opis/Brosura_o_obdavcitvi_nepremicnin.docx' },
                ],
              },
            ],
          },
          {
            h2: 'Why does it differ from the market price?',
            blocks: [
              {
                p: 'Because it is modeled, the generalized value does not account for individual condition: quality of renovation, view, floor, noise or damp. It also reflects the price level at the model’s valuation date and does not follow the market in real time. So for a specific unit it can deviate from the achieved market price. For a market estimate, always combine it with realized prices (ETN) and current listings.',
              },
              {
                callout: {
                  text: 'The generalized value is not an official appraisal. A bank or court requires an appraisal by a licensed valuer.',
                },
              },
            ],
          },
          {
            h2: 'Where can I check the generalized value?',
            blocks: [
              {
                p: 'You can check your property’s generalized value via the Prostor portal (GURS), which provides public property data.',
              },
              {
                sources: [
                  { label: 'GURS — about mass real-estate valuation (e-prostor, in Slovenian)', href: 'https://www.e-prostor.gov.si/podrocja/trg-in-vrednosti-nepremicnin/drugo/razno/o-mnozicnem-vrednotenju/' },
                  { label: 'Prostor portal (public access)', href: 'https://www.e-prostor.gov.si/' },
                ],
              },
            ],
          },
        ],
        faq: [
          {
            question: 'Is the generalized value the same as market value?',
            answer:
              'No. The generalized value is a modeled estimate for public purposes and ignores the condition of the specific unit, so it can deviate from the actual market price.',
          },
          {
            question: 'Who sets the generalized value?',
            answer:
              'The Surveying and Mapping Authority of Slovenia (GURS), as part of the mass real-estate valuation system.',
          },
          {
            question: 'Can I use the generalized value for a bank loan?',
            answer:
              'Not as an official basis. Banks require an appraisal by a licensed real-estate valuer; the generalized value is only a reference.',
          },
        ],
      },
    },
  },

  // ---------------------------------------------------------------------- etn
  {
    group: 'etn',
    datePublished: PUBLISHED,
    dateModified: '2026-09-29',
    changefreq: 'monthly',
    priority: '0.7',
    locales: {
      sl: {
        path: '/vodnik/etn-evidenca-trga-nepremicnin',
        title: 'ETN — Evidenca trga nepremičnin: kaj je in kako jo brati | nReport',
        description:
          'Kaj je ETN (evidenca trga nepremičnin), katere podatke o realiziranih prodajah in najemih vsebuje, kdo jo vodi in kako jo uporabiti za oceno tržne cene.',
        breadcrumb: 'ETN — evidenca trga nepremičnin',
        eyebrow: 'Pojmi',
        h1: 'Kaj je ETN (evidenca trga nepremičnin)?',
        intro:
          'ETN je uradna evidenca trga nepremičnin, ki jo vodi Geodetska uprava RS. Vsebuje podatke o dejansko realiziranih prodajah in najemih nepremičnin v Sloveniji — torej cene, po katerih so bili posli res sklenjeni. Ker temelji na resničnih transakcijah, je najboljši javni pokazatelj tržne cene.',
        sections: [
          {
            h2: 'Katere podatke vsebuje ETN?',
            blocks: [
              {
                ul: [
                  'pogodbeno ceno realiziranih prodaj in najemnin,',
                  'osnovne lastnosti nepremičnine (tip, površina, leto),',
                  'lokacijo, določeno s katastrsko občino ter številko stavbe, dela stavbe oz. parcele,',
                  'datum sklenitve posla.',
                ],
              },
            ],
          },
          {
            h2: 'Zakaj so realizirane cene boljše od oglaševanih?',
            blocks: [
              {
                p: 'Oglaševana cena je pričakovanje prodajalca in je pogosto višja od tiste, po kateri se nepremičnina na koncu proda. Realizirana cena iz ETN je dokaz, za koliko je kupec dejansko plačal. Razlika med oglaševano in realizirano ceno pove tudi, koliko prostora za pogajanje običajno obstaja na določenem trgu.',
              },
              {
                callout: {
                  title: 'Nasvet',
                  text: 'Za oceno vrednosti primerjajte ceno na m² realiziranih poslov v istem naselju in podobnem obdobju, ne le trenutnih oglasov.',
                },
              },
            ],
          },
          {
            h2: 'Kako uporabiti ETN pri oceni vrednosti',
            blocks: [
              {
                steps: [
                  { title: 'Izberite primerljive posle', text: 'Enak tip nepremičnine, podobna velikost in lokacija, po možnosti zadnjih 12–24 mesecev.' },
                  { title: 'Izračunajte ceno na m²', text: 'Realizirano ceno delite s površino, da dobite primerljivo mero.' },
                  { title: 'Upoštevajte trend', text: 'Če cene v obdobju rastejo ali padajo, starejše posle ustrezno indeksirajte.' },
                ],
              },
            ],
          },
          {
            h2: 'Kje so podatki ETN dostopni?',
            blocks: [
              {
                sources: [
                  { label: 'GURS — trg nepremičnin in evidenca trga nepremičnin (ETN, e-prostor)', href: 'https://www.e-prostor.gov.si/podrocja/trg-in-vrednosti-nepremicnin/trg-nepremicnin/' },
                  { label: 'Portal Prostor (javni podatki in poročila o trgu)', href: 'https://www.e-prostor.gov.si/' },
                ],
              },
            ],
          },
        ],
        faq: [
          {
            question: 'Kdo vodi evidenco trga nepremičnin?',
            answer:
              'Geodetska uprava Republike Slovenije (GURS). Evidenca se polni iz napovedi za davek na promet nepremičnin, ki jih posreduje Finančna uprava, ter iz poročil prodajalcev (za posle z DDV), najemodajalcev, najemnikov in upravnikov stavb.',
          },
          {
            question: 'So podatki ETN javni in brezplačni?',
            answer:
              'Del podatkov o realiziranih poslih je javno dostopen prek portala Prostor. nReport te podatke združi in primerja z GURS vrednostjo in oglasi v enem poročilu.',
          },
          {
            question: 'Kako sveži so podatki v ETN?',
            answer:
              'Podatki imajo praviloma nekaj mesecev zamika, ker se posli evidentirajo po sklenitvi in obdelavi. Za redke tipe nepremičnin je poslov malo.',
          },
        ],
      },
      en: {
        path: '/en/guide/etn-property-transaction-register',
        title: 'ETN — Slovenia’s Property Transaction Register Explained | nReport',
        description:
          'What ETN (the real estate transaction register) is, what data on realized sales and rentals it contains, who maintains it, and how to use it to estimate market price.',
        breadcrumb: 'ETN — transaction register',
        eyebrow: 'Concepts',
        h1: 'What Is ETN (the Real Estate Transaction Register)?',
        intro:
          'ETN is Slovenia’s official real estate transaction register, maintained by the Surveying and Mapping Authority (GURS). It records actual realized sales and rentals — the prices at which deals were genuinely concluded. Because it is based on real transactions, it is the best public indicator of market price.',
        sections: [
          {
            h2: 'What data does ETN contain?',
            blocks: [
              {
                ul: [
                  'the contract price of realized sales and rents,',
                  'basic property characteristics (type, area, year),',
                  'the location, identified by cadastral municipality and building, building-part or parcel number,',
                  'the date the deal was concluded.',
                ],
              },
            ],
          },
          {
            h2: 'Why are realized prices better than asking prices?',
            blocks: [
              {
                p: 'An asking price is the seller’s expectation and is often higher than the price the property eventually sells for. A realized price from ETN is proof of what a buyer actually paid. The gap between asking and realized prices also tells you how much negotiation room typically exists in a given market.',
              },
              {
                callout: {
                  title: 'Tip',
                  text: 'To estimate value, compare the price per m² of realized deals in the same settlement and a similar period — not just current listings.',
                },
              },
            ],
          },
          {
            h2: 'How to use ETN in a value estimate',
            blocks: [
              {
                steps: [
                  { title: 'Select comparable deals', text: 'Same property type, similar size and location, ideally from the last 12–24 months.' },
                  { title: 'Compute price per m²', text: 'Divide the realized price by the area to get a comparable measure.' },
                  { title: 'Account for the trend', text: 'If prices are rising or falling over the period, index older deals accordingly.' },
                ],
              },
            ],
          },
          {
            h2: 'Where is ETN data available?',
            blocks: [
              {
                sources: [
                  { label: 'GURS — property market and the transaction register (ETN, e-prostor, in Slovenian)', href: 'https://www.e-prostor.gov.si/podrocja/trg-in-vrednosti-nepremicnin/trg-nepremicnin/' },
                  { label: 'Prostor portal (public data and market reports)', href: 'https://www.e-prostor.gov.si/' },
                ],
              },
            ],
          },
        ],
        faq: [
          {
            question: 'Who maintains the transaction register?',
            answer:
              'The Surveying and Mapping Authority of Slovenia (GURS). The register is populated from real estate transfer tax returns, passed on by the Financial Administration, and from reports by sellers (for deals subject to VAT), landlords, tenants and building managers.',
          },
          {
            question: 'Is ETN data public and free?',
            answer:
              'Part of the realized-deal data is publicly available via the Prostor portal. nReport combines this data and compares it with the GURS value and listings in a single report.',
          },
          {
            question: 'How fresh is ETN data?',
            answer:
              'Data typically has a few months’ lag because deals are recorded after conclusion and processing. For rare property types there are few deals.',
          },
        ],
      },
    },
  },

  // ------------------------------------------------------------- due-diligence
  {
    group: 'due-diligence',
    datePublished: PUBLISHED,
    dateModified: '2026-09-29',
    changefreq: 'monthly',
    priority: '0.8',
    locales: {
      sl: {
        path: '/vodnik/preverjanje-nepremicnine-pred-nakupom',
        title: 'Kako preveriti nepremičnino pred nakupom v Sloveniji | nReport',
        description:
          'Kontrolni seznam pred nakupom nepremičnine v Sloveniji: zemljiška knjiga (lastništvo in bremena), GURS podatki, energetska izkaznica, realne cene in pogosta tveganja.',
        breadcrumb: 'Preverjanje pred nakupom',
        eyebrow: 'Nakup',
        h1: 'Kako preveriti nepremičnino pred nakupom',
        intro:
          'Pred nakupom nepremičnine v Sloveniji vedno preverite tri stvari: pravno stanje v zemljiški knjigi (kdo je lastnik in ali obstajajo bremena), skladnost dejanskega stanja z uradnimi evidencami (GURS) in realno tržno vrednost. Tako se izognete dragim presenečenjem — hipotekam, služnostim ali precenjeni ceni.',
        sections: [
          {
            h2: 'Kontrolni seznam pred nakupom',
            blocks: [
              {
                ol: [
                  'Zemljiška knjiga: preverite lastnika, hipoteke, služnosti in morebitne plombe (nerešeni vpisi).',
                  'GURS / kataster: preverite, ali se površina, tip in namembnost ujemajo z resničnim stanjem.',
                  'Energetska izkaznica: ob prodaji je obvezna; pove energetski razred in okvirne stroške.',
                  'Vrednost: primerjajte ceno z realiziranimi posli (ETN) in GURS vrednostjo.',
                  'Etažna lastnina in stroški: preverite delež skupnih prostorov, rezervni sklad in obratovalne stroške.',
                ],
              },
            ],
          },
          {
            h2: 'Kaj pove zemljiška knjiga?',
            blocks: [
              {
                p: 'Zemljiška knjiga je uradna javna evidenca pravnega stanja nepremičnin, ki jo vodijo sodišča. Iz nje razberete lastništvo in vsa bremena. Najpomembnejši so:',
              },
              {
                table: {
                  head: ['Vpis', 'Kaj pomeni', 'Zakaj je pomembno'],
                  rows: [
                    ['Hipoteka', 'Nepremičnina je zastavljena za posojilo', 'Breme mora biti pred prenosom izbrisano ali poplačano'],
                    ['Služnost', 'Pravica tretjega (npr. pot, dostop)', 'Lahko omejuje uporabo nepremičnine'],
                    ['Plomba', 'Nerešen, še ne vpisan predlog', 'Pravno stanje se lahko še spremeni'],
                    ['Prepoved odtujitve', 'Omejitev razpolaganja', 'Prodaja morda ni mogoča brez soglasja'],
                  ],
                },
              },
              {
                p: 'Javni vpogled v zemljiško knjigo je brezplačno dostopen prek portala eSodstvo. Od junija 2026 zahteva prijavo s SI-PASS, pri čemer zadošča brezplačni smsPASS.',
              },
            ],
          },
          {
            h2: 'Pogosta tveganja, ki jih spregledamo',
            blocks: [
              {
                ul: [
                  'neizbrisana hipoteka prejšnjega lastnika,',
                  'služnostna pot čez parcelo, ki je ni na oglasu,',
                  'neusklajenost dejanske in evidentirane površine,',
                  'oglaševana cena bistveno nad realiziranimi cenami v okolici,',
                  'nelegalizirani prizidki ali spremembe namembnosti.',
                ],
              },
              {
                callout: {
                  title: 'Preden podpišete',
                  text: 'Zemljiškoknjižni izpisek naj bo star nekaj dni, ne mesecev — stanje se lahko spremeni. Ob nejasnostih se posvetujte z notarjem ali odvetnikom.',
                },
              },
            ],
          },
          {
            h2: 'Viri in uradni vpogledi',
            blocks: [
              {
                sources: [
                  { label: 'Zemljiška knjiga — e-Sodstvo (javni vpogled)', href: 'https://esodisce.si/' },
                  { label: 'GURS — podatki o nepremičninah (portal Prostor)', href: 'https://www.e-prostor.gov.si/' },
                  { label: 'Energetska izkaznica — Ministrstvo (register)', href: 'https://www.energetika-portal.si/podrocja/energetika/energetske-izkaznice-stavb/' },
                ],
              },
            ],
          },
        ],
        faq: [
          {
            question: 'Kje brezplačno preverim lastništvo nepremičnine?',
            answer:
              'V zemljiški knjigi prek portala e-Sodstvo je javni vpogled brezplačen. Pokaže lastnika in vpisana bremena (hipoteke, služnosti, plombe).',
          },
          {
            question: 'Je energetska izkaznica ob nakupu obvezna?',
            answer:
              'Pri prodaji jo mora zagotoviti prodajalec. Pove energetski razred stavbe in okvirno raven stroškov ogrevanja.',
          },
          {
            question: 'Kako vem, ali je cena poštena?',
            answer:
              'Primerjajte oglaševano ceno z realiziranimi posli iz ETN in GURS posplošeno vrednostjo. Če je oglaševana cena bistveno višja od realiziranih cen v okolici, je verjeten prostor za pogajanje.',
          },
        ],
      },
      en: {
        path: '/en/guide/property-checks-before-buying-slovenia',
        title: 'How to Check a Property Before Buying in Slovenia | nReport',
        description:
          'A pre-purchase checklist for property in Slovenia: the land register (ownership and encumbrances), GURS data, the energy certificate, realistic prices and common risks.',
        breadcrumb: 'Pre-purchase checks',
        eyebrow: 'Buying',
        h1: 'How to Check a Property Before Buying',
        intro:
          'Before buying a property in Slovenia, always check three things: the legal status in the land register (who owns it and whether there are encumbrances), whether the actual state matches the official records (GURS), and the realistic market value. This avoids costly surprises — mortgages, easements or an overpriced deal.',
        sections: [
          {
            h2: 'Pre-purchase checklist',
            blocks: [
              {
                ol: [
                  'Land register: check the owner, mortgages, easements and any pending entries.',
                  'GURS / cadastre: check that the area, type and use match reality.',
                  'Energy certificate: mandatory when selling; shows the energy class and rough costs.',
                  'Value: compare the price against realized deals (ETN) and the GURS value.',
                  'Condominium and costs: check the share of common areas, reserve fund and running costs.',
                ],
              },
            ],
          },
          {
            h2: 'What does the land register tell you?',
            blocks: [
              {
                p: 'The land register is the official public record of the legal status of property, maintained by the courts. It shows ownership and all encumbrances. The most important are:',
              },
              {
                table: {
                  head: ['Entry', 'What it means', 'Why it matters'],
                  rows: [
                    ['Mortgage', 'The property is pledged for a loan', 'The encumbrance must be discharged or repaid before transfer'],
                    ['Easement', 'A third party’s right (e.g. path, access)', 'It can restrict use of the property'],
                    ['Pending entry', 'An unresolved, not-yet-registered proposal', 'The legal status may still change'],
                    ['Ban on disposal', 'A restriction on transfer', 'A sale may not be possible without consent'],
                  ],
                },
              },
              {
                p: 'Public access to the land register is available free of charge via the e-Justice (eSodstvo) portal. Since June 2026 it requires a SI-PASS login; the free smsPASS is enough.',
              },
            ],
          },
          {
            h2: 'Common risks people overlook',
            blocks: [
              {
                ul: [
                  'an undischarged mortgage from the previous owner,',
                  'an easement path across the plot that is not in the listing,',
                  'a mismatch between the actual and recorded area,',
                  'an asking price well above realized prices nearby,',
                  'unlegalized extensions or changes of use.',
                ],
              },
              {
                callout: {
                  title: 'Before you sign',
                  text: 'A land-register extract should be days old, not months — the status can change. If anything is unclear, consult a notary or lawyer.',
                },
              },
            ],
          },
          {
            h2: 'Sources and official records',
            blocks: [
              {
                sources: [
                  { label: 'Land register — e-Justice (public access)', href: 'https://esodisce.si/' },
                  { label: 'GURS — property data (Prostor portal)', href: 'https://www.e-prostor.gov.si/' },
                  { label: 'Energy portal — official register of energy certificates (in Slovenian)', href: 'https://www.energetika-portal.si/podrocja/energetika/energetske-izkaznice-stavb/register-energetskih-izkaznic/' },
                ],
              },
            ],
          },
        ],
        faq: [
          {
            question: 'Where can I check property ownership for free?',
            answer:
              'In the land register via the e-Justice portal, public access is free. It shows the owner and registered encumbrances (mortgages, easements, pending entries).',
          },
          {
            question: 'Is an energy certificate mandatory when buying?',
            answer:
              'The seller must provide one when selling. It shows the building’s energy class and a rough level of heating costs.',
          },
          {
            question: 'How do I know if the price is fair?',
            answer:
              'Compare the asking price with realized deals from ETN and the GURS generalized value. If the asking price is well above realized prices nearby, there is likely room to negotiate.',
          },
        ],
      },
    },
  },
  // ------------------------------------------------------- asking vs realized
  // The strategy review calls this the strongest educational hook we have:
  // most Slovenian buyers do not know that asking prices sit above realized
  // ones, and the gap is simultaneously the demonstration and the reason to
  // buy. It is written with the same restraint the analysis pipeline applies —
  // the gap is NOT all negotiating room, and saying so is what makes the rest
  // credible.
  {
    group: 'asking-vs-realized',
    datePublished: '2026-08-09',
    dateModified: '2026-08-09',
    changefreq: 'monthly',
    priority: '0.8',
    locales: {
      sl: {
        path: '/vodnik/oglasevana-cena-proti-realizirani',
        title: 'Oglaševana cena proti realizirani: kolikšna je razlika? (2026) | nReport',
        description:
          'Zakaj se oglaševane cene nepremičnin razlikujejo od realiziranih, koliko znaša razlika v Sloveniji in zakaj celotne razlike ni mogoče razumeti kot pogajalski prostor.',
        breadcrumb: 'Oglaševana proti realizirani ceni',
        eyebrow: 'Cene',
        h1: 'Oglaševana cena proti realizirani ceni',
        intro:
          'Oglas pravi 250.000 €. Sosednje stanovanje se je prodalo za 218.000 €. Katera številka je resnica? Realizirana — ker je to znesek, ki ga je nekdo dejansko plačal. Oglaševana cena je pričakovanje prodajalca in je praviloma višja. Razlika pa ni v celoti pogajalski prostor, in prav to je najpogostejši nesporazum.',
        sections: [
          {
            h2: 'Kaj je razlika med oglaševano in realizirano ceno?',
            blocks: [
              {
                p: 'Oglaševana cena je znesek, po katerem je nepremičnina ponujena na oglasnih portalih. Realizirana cena je znesek, ki je bil ob prodaji prijavljen v evidenco trga nepremičnin (ETN), ki jo vodi Geodetska uprava Republike Slovenije. Prva izraža pričakovanje, druga izid.',
              },
              {
                table: {
                  head: ['', 'Oglaševana cena', 'Realizirana cena (ETN)'],
                  rows: [
                    ['Kaj pove', 'Kaj prodajalec zahteva', 'Kaj je kupec plačal'],
                    ['Vir', 'Oglasni portali', 'Evidenca trga nepremičnin (GURS)'],
                    ['Ažurnost', 'Takojšnja', 'Zamik nekaj tednov do mesecev'],
                    ['Zanesljivost', 'Izhodišče za pogajanja', 'Dejansko sklenjen posel'],
                  ],
                },
              },
              {
                callout: {
                  title: 'Zakaj to sploh preverjati',
                  text: 'Kupec, ki pozna realizirane cene v okolici, se pogaja z dejstvi. Kupec, ki pozna samo oglaševane cene, se pogaja z občutkom — in edini v pogovoru, ki pozna realizirane cene, je takrat prodajalčev agent.',
                },
              },
            ],
          },
          {
            h2: 'Kolikšna je razlika v Sloveniji?',
            blocks: [
              {
                p: 'Razlika se močno razlikuje po lokaciji, tipu nepremičnine in stanju trga, zato ene same številke za vso Slovenijo ni. Pri posameznem naslovu jo je mogoče izračunati — primerjava oglaševanih cen primerljivih nepremičnin v okolici z realiziranimi posli iz ETN v istem območju.',
              },
              {
                p: 'Pomembneje od povprečja je, da razlike ne razlagate napačno. Del razlike so pogajanja, del pa izvira iz dveh povsem drugih razlogov.',
              },
            ],
          },
          {
            h2: 'Zakaj razlika ni v celoti pogajalski prostor',
            blocks: [
              {
                p: 'To je najpogostejša napaka pri branju te razlike. Trije dejavniki jo ustvarjajo, in samo prvi je pogajalski:',
              },
              {
                ol: [
                  'Pogajanja. Izhodiščna cena je praviloma postavljena nekoliko višje, da ostane prostor za dogovor. To je edini del razlike, ki ga kupec dejansko lahko uveljavlja.',
                  'Različne nepremičnine. Oglasi in realizirani posli niso iste nepremičnine: razlikujejo se po velikosti, stanju in legi, posli so različno stari, med aktivnimi oglasi pa so nadpovprečno zastopani tisti, ki se težje prodajo, ker dlje ostanejo na trgu.',
                  'Različen geografski zajem. Oglasi so praviloma zajeti s širšega območja kot realizirani posli v neposredni okolici, zato se primerjata dve nekoliko različni množici nepremičnin.',
                ],
              },
              {
                callout: {
                  title: 'Praktična posledica',
                  text: 'Če je razlika med oglaševanimi in realiziranimi cenami v vaši okolici na primer 15 %, to ne pomeni, da lahko ponudite 15 % manj. Pomeni, da je izhodiščna cena praviloma nad ceno, po kateri se posli sklenejo — realen pogajalski prostor je manjši.',
                },
              },
            ],
          },
          {
            h2: 'Kako to preverite sami',
            blocks: [
              {
                steps: [
                  {
                    title: 'Poiščite realizirane posle v okolici',
                    text: 'Evidenca trga nepremičnin je javna. Iščite posle v istem naselju oziroma v radiju nekaj sto metrov, po možnosti iz zadnjih dveh let.',
                  },
                  {
                    title: 'Primerjajte primerljivo',
                    text: 'Upoštevajte le nepremičnine podobne velikosti in vrste. Trisobno stanovanje in garsonjera na isti ulici imata povsem različno ceno na m².',
                  },
                  {
                    title: 'Upoštevajte starost podatka',
                    text: 'Posel iz leta 2019 pri isti ceni danes pomeni drugačno vrednost. Starejše posle preračunajte na današnjo raven ali jim dajte manjšo težo.',
                  },
                  {
                    title: 'Bodite pozorni na osamelce',
                    text: 'Zelo nizka cena na m² pogosto pomeni prodajo deleža ali prenos med povezanimi osebami, ne tržnega posla. Takšni posli niso merilo.',
                  },
                ],
              },
              {
                sources: [
                  { label: 'GURS — trg nepremičnin in evidenca trga nepremičnin (e-prostor)', href: 'https://www.e-prostor.gov.si/podrocja/trg-in-vrednosti-nepremicnin/trg-nepremicnin/' },
                  { label: 'Portal Prostor (javni vpogled)', href: 'https://www.e-prostor.gov.si/' },
                ],
              },
            ],
          },
        ],
        faq: [
          {
            question: 'Za koliko so oglaševane cene višje od realiziranih?',
            answer:
              'Razlika se razlikuje po lokaciji in tipu nepremičnine, zato ene številke za vso Slovenijo ni. Za konkreten naslov jo je mogoče izračunati iz primerjave oglasov v okolici z realiziranimi posli iz ETN.',
          },
          {
            question: 'Ali lahko ponudim toliko manj, kolikor znaša razlika?',
            answer:
              'Ne. Del razlike so pogajanja, del pa razlike med primerjanimi nepremičninami in širši geografski zajem oglasov. Realen pogajalski prostor je praviloma manjši od celotne razlike.',
          },
          {
            question: 'Kje so realizirane cene javno dostopne?',
            answer:
              'V evidenci trga nepremičnin (ETN), ki jo vodi Geodetska uprava Republike Slovenije in je javno dostopna prek portala Prostor.',
          },
          {
            question: 'Zakaj sta dva posla na istem naslovu tako različna?',
            answer:
              'Najpogostejši razlogi so prodaja deleža nepremičnine namesto celote, prenos med povezanimi osebami po simbolični ceni ali obnova med obema prodajama. Takšnih poslov ne uporabljajte kot merilo tržne cene.',
          },
        ],
      },
      en: {
        path: '/en/guide/asking-vs-realized-prices-slovenia',
        title: 'Asking vs Realized Prices in Slovenia (2026) | nReport',
        description:
          'Why property asking prices differ from realized prices in Slovenia, how large the gap is, and why the whole gap cannot be treated as negotiating room.',
        breadcrumb: 'Asking vs realized prices',
        eyebrow: 'Prices',
        h1: 'Asking vs Realized Prices in Slovenia',
        intro:
          'The listing says €250,000. The neighbouring apartment sold for €218,000. Which number is true? The realized one — because somebody actually paid it. An asking price is the seller’s expectation and is normally higher. But the gap is not all negotiating room, and that is the most common misreading.',
        sections: [
          {
            h2: 'What is the difference between asking and realized prices?',
            blocks: [
              {
                p: 'The asking price is what a property is offered for on listing portals. The realized price is what was reported to the real estate transaction register (ETN), maintained by the Surveying and Mapping Authority of Slovenia, when the sale completed. The first expresses an expectation; the second, an outcome.',
              },
              {
                table: {
                  head: ['', 'Asking price', 'Realized price (ETN)'],
                  rows: [
                    ['What it tells you', 'What the seller wants', 'What a buyer paid'],
                    ['Source', 'Listing portals', 'Transaction register (GURS)'],
                    ['Timeliness', 'Immediate', 'Weeks to months of lag'],
                    ['Reliability', 'A negotiating starting point', 'A completed transaction'],
                  ],
                },
              },
              {
                callout: {
                  title: 'Why check at all',
                  text: 'A buyer who knows realized prices nearby negotiates with facts. A buyer who knows only asking prices negotiates on instinct — and the only person in the room who does know the realized prices is the seller’s agent.',
                },
              },
            ],
          },
          {
            h2: 'How large is the gap in Slovenia?',
            blocks: [
              {
                p: 'It varies considerably by location, property type and market conditions, so there is no single nationwide figure. For an individual address it can be computed: comparing asking prices of similar nearby properties against realized ETN transactions in the same area.',
              },
              {
                p: 'More important than the average is not misreading it. Part of the gap is negotiation; the rest comes from two entirely different causes.',
              },
            ],
          },
          {
            h2: 'Why the gap is not all negotiating room',
            blocks: [
              {
                p: 'This is the most common error when reading the gap. Three factors create it, and only the first is negotiable:',
              },
              {
                ol: [
                  'Negotiation. Asking prices are usually set somewhat high to leave room for agreement. This is the only part a buyer can genuinely claim.',
                  'Different properties. Listings and realized sales are not the same properties: they differ in size, condition and location, the sales date from different times, and active listings over-represent slower sellers because those stay on the market longer.',
                  'Different geographic coverage. Listings are typically drawn from a wider area than realized sales in the immediate vicinity, so the two sets are not quite the same properties.',
                ],
              },
              {
                callout: {
                  title: 'What this means in practice',
                  text: 'If asking prices in your area sit, say, 15 % above realized ones, that does not mean you can offer 15 % less. It means the starting price is normally above the price at which deals close — the real negotiating room is smaller.',
                },
              },
            ],
          },
          {
            h2: 'How to check this yourself',
            blocks: [
              {
                steps: [
                  {
                    title: 'Find realized sales nearby',
                    text: 'The transaction register is public. Look for sales in the same settlement or within a few hundred metres, ideally from the last two years.',
                  },
                  {
                    title: 'Compare like with like',
                    text: 'Only include properties of similar size and type. A three-room apartment and a studio on the same street have very different prices per m².',
                  },
                  {
                    title: 'Account for the age of the data',
                    text: 'A 2019 sale at the same price means something different today. Restate older sales at today’s level, or give them less weight.',
                  },
                  {
                    title: 'Watch for outliers',
                    text: 'A very low price per m² often means a share was sold, or a transfer between related parties, rather than a market sale. Those are not a benchmark.',
                  },
                ],
              },
              {
                sources: [
                  { label: 'GURS — property market and the transaction register (e-prostor, in Slovenian)', href: 'https://www.e-prostor.gov.si/podrocja/trg-in-vrednosti-nepremicnin/trg-nepremicnin/' },
                  { label: 'Prostor portal (public access)', href: 'https://www.e-prostor.gov.si/' },
                ],
              },
            ],
          },
        ],
        faq: [
          {
            question: 'How much higher are asking prices than realized prices?',
            answer:
              'It varies by location and property type, so there is no single figure for Slovenia. For a specific address it can be computed by comparing nearby listings against realized ETN transactions.',
          },
          {
            question: 'Can I offer less by the full size of the gap?',
            answer:
              'No. Part of the gap is negotiation, but part comes from differences between the compared properties and from listings covering a wider area than realized sales. The real negotiating room is normally smaller than the whole gap.',
          },
          {
            question: 'Where are realized prices publicly available?',
            answer:
              'In the real estate transaction register (ETN), maintained by the Surveying and Mapping Authority of Slovenia and publicly accessible through the Prostor portal.',
          },
          {
            question: 'Why do two sales at the same address differ so much?',
            answer:
              'The usual causes are a share of the property being sold rather than the whole, a transfer between related parties at a nominal price, or a renovation between the two dates. Such sales are not a benchmark for market price.',
          },
        ],
      },
    },
  },

  // ---------------------------------------------------------------- glossary
  // The glossary is the one guide built from `terms` blocks: each term becomes
  // a DefinedTerm in a DefinedTermSet (scripts/seo-config.js). Every
  // definition paraphrases an official text — the Zakon o katastru
  // nepremičnin, the land-register law (ZZK-1), the Stvarnopravni zakonik, the
  // GURS e-prostor pages or the FURS brochure — and the sources blocks name
  // them. Terms are kept in Slovenian on both locales because that is how they
  // appear in registers, contracts and the report itself.
  {
    group: 'glossary',
    datePublished: '2026-09-28',
    dateModified: '2026-09-28',
    changefreq: 'monthly',
    priority: '0.7',
    locales: {
      sl: {
        path: '/vodnik/slovar-nepremicninskih-pojmov',
        title: 'Slovar nepremičninskih pojmov (2026) | nReport',
        description:
          'Razlaga pojmov, ki jih srečate pri nakupu nepremičnine v Sloveniji: GURS, ETN, zemljiška knjiga, kataster, posplošena vrednost, plomba, služnost in drugi.',
        breadcrumb: 'Slovar nepremičninskih pojmov',
        eyebrow: 'Pojmi',
        h1: 'Slovar nepremičninskih pojmov',
        intro:
          'Pri nakupu nepremičnine v Sloveniji naletite na pojme iz treh uradnih svetov: katastra nepremičnin (kaj nepremičnina fizično je), zemljiške knjige (kdo ima nanjo kakšne pravice) in evidenc trga ter vrednotenja (koliko je vredna). Ta slovar jih razloži v enem mestu, vsak pojem pa se opira na zakon ali uradno stran, navedeno pod razdelkom.',
        sections: [
          {
            h2: 'Uradne evidence in institucije',
            blocks: [
              {
                terms: [
                  {
                    term: 'GURS',
                    alternateName: 'Geodetska uprava Republike Slovenije',
                    definition:
                      'Državni organ, ki vodi kataster nepremičnin, evidenco trga nepremičnin (ETN) in evidenco vrednotenja s posplošenimi vrednostmi. Njeni podatki so javno dostopni prek portala Prostor (e-prostor.gov.si).',
                  },
                  {
                    term: 'Kataster nepremičnin',
                    definition:
                      'Uradna evidenca podatkov o parcelah, stavbah in delih stavb — o njihovem položaju, obliki, površini in drugih fizičnih lastnostih. Izkazuje dejansko stanje nepremičnin in omogoča vpis pravic v zemljiško knjigo. Po Zakonu o katastru nepremičnin (ZKN) je nadomestil tri prejšnje evidence: zemljiški kataster, kataster stavb in register nepremičnin; informacijski sistem katastra se uporablja od 4. aprila 2022.',
                  },
                  {
                    term: 'Zemljiška knjiga',
                    definition:
                      'Javna knjiga, namenjena vpisu in javni objavi podatkov o pravicah na nepremičninah (lastninska pravica, hipoteka, služnost, stavbna pravica …) in pravnih dejstvih v zvezi z njimi. Vodijo jo okrajna sodišča, vsi vpisi so javni. Kdor pošteno ravna in se zanese na vpisane podatke, zaradi tega ne sme trpeti škodljivih posledic.',
                  },
                  {
                    term: 'ETN',
                    alternateName: 'evidenca trga nepremičnin',
                    definition:
                      'Javna evidenca kupoprodajnih in najemnih poslov z nepremičninami, ki jo vodi GURS. Zajema cene in najemnine, dosežene v sklenjenih poslih, ter podatke o nepremičninah, ki so bile predmet posla; najemni posli se evidentirajo, če so sklenjeni za najmanj šest mesecev. Osebni podatki pogodbenih strank niso javni.',
                  },
                ],
              },
              {
                sources: [
                  { label: 'GURS — kataster nepremičnin (e-prostor)', href: 'https://www.e-prostor.gov.si/podrocja/parcele-in-stavbe/kataster-nepremicnin/' },
                  { label: 'Zakon o katastru nepremičnin (ZKN), Uradni list RS, št. 54/21', href: 'https://www.uradni-list.si/glasilo-uradni-list-rs/vsebina/2021-01-1047' },
                  { label: 'Sodstvo RS — zemljiška knjiga', href: 'https://www.sodisce.si/javne_knjige/zemljiska_knjiga/' },
                  { label: 'GURS — trg nepremičnin in ETN (e-prostor)', href: 'https://www.e-prostor.gov.si/podrocja/trg-in-vrednosti-nepremicnin/trg-nepremicnin/' },
                ],
              },
            ],
          },
          {
            h2: 'Nepremičnina, parcela in površina',
            blocks: [
              {
                terms: [
                  {
                    term: 'Katastrska občina',
                    alternateName: 'KO',
                    definition:
                      'Osnovna enota, v kateri se vodijo podatki katastra nepremičnin. Vsaka parcela in stavba je v Sloveniji enolično določena s šifro katastrske občine in številko znotraj nje. Katastrska občina ni isto kot občina.',
                  },
                  {
                    term: 'Parcela',
                    alternateName: 'parcelna številka',
                    definition:
                      'Prostorsko odmerjeno zemljišče, ki leži v eni katastrski občini, je v katastru nepremičnin vpisano z mejo in označeno s parcelno številko. Parcelna številka se izpiše kot npr. »parcela 123 24/2«: šifra katastrske občine, številka parcele in po potrebi poddelilka za poševnico.',
                  },
                  {
                    term: 'Stavba',
                    definition:
                      'Pokrit objekt, v katerega se lahko vstopi in je namenjen bivanju ali opravljanju dejavnosti. Stavba ima vsaj en del stavbe.',
                  },
                  {
                    term: 'Del stavbe',
                    definition:
                      'Funkcionalna celota prostorov v stavbi, primerna za samostojno uporabo — na primer stanovanje ali poslovni prostor; v stavbi z etažno lastnino je to posamezni del v etažni lastnini. Številko dela stavbe sestavljata številka stavbe in številka, določena znotraj stavbe.',
                  },
                  {
                    term: 'Neto tlorisna površina dela stavbe',
                    definition:
                      'Vsota površin vseh prostorov, ki pripadajo delu stavbe. Pri stanovanju vključuje tudi pripadajoče prostore zunaj bivalnega dela, na primer klet v drugi etaži. To je površina, ki jo kataster nepremičnin vodi kot površino dela stavbe; nReport jo uporablja kot osnovo za ceno na m².',
                  },
                  {
                    term: 'Uporabna površina dela stavbe',
                    definition:
                      'Vsota površin prostorov, ki se uporabljajo za enak namen kot del stavbe. Pri stanovanju se vanjo štejejo bivalni prostori, ne pa klet, balkon, terasa, loža ali garaža — zato je manjša od neto tlorisne površine. Cena na m² uporabne površine ni neposredno primerljiva s ceno na m² neto tlorisne površine.',
                  },
                  {
                    term: 'Etažna lastnina',
                    definition:
                      'Lastnina posameznega dela zgradbe (npr. stanovanja) in solastnina skupnih delov stavbe. Etažna lastnina je predmet razpolaganja kot celota: s stanovanjem se proda tudi pripadajoči solastniški delež skupnih delov.',
                  },
                  {
                    term: 'Rezervni sklad',
                    definition:
                      'Sredstva etažnih lastnikov za kritje stroškov vzdrževanja in potrebnih izboljšav stavbe. So skupno premoženje etažnih lastnikov, upravnik jih vodi na posebnem računu, etažni lastnik pa vračila vplačil ne more zahtevati — tudi ob prodaji stanovanja ne.',
                  },
                ],
              },
              {
                sources: [
                  { label: 'GURS — kataster nepremičnin: parcele, stavbe, deli stavb (e-prostor)', href: 'https://www.e-prostor.gov.si/podrocja/parcele-in-stavbe/kataster-nepremicnin/' },
                  { label: 'Pravilnik o vodenju podatkov katastra nepremičnin, Priloga 3 (merjenje in uporabna površina)', href: 'https://www.uradni-list.si/files/RS_-2022-041-00786-OB~P006-0000.PDF' },
                  { label: 'Stvarnopravni zakonik (SPZ) — PISRS', href: 'https://pisrs.si/pregledPredpisa?id=ZAKO3242' },
                ],
              },
            ],
          },
          {
            h2: 'Pravice in bremena v zemljiški knjigi',
            blocks: [
              {
                terms: [
                  {
                    term: 'Vknjižba, predznamba in zaznamba',
                    definition:
                      'Glavni vpisi v zemljiško knjigo. Z vknjižbo se doseže ali izkaže pridobitev ali prenehanje pravice; predznamba učinkuje enako, a pod pogojem, da se kasneje opraviči; z zaznambo se vpisujejo pravna dejstva, na primer spor ali izvršba.',
                  },
                  {
                    term: 'Plomba',
                    definition:
                      'Pomožni vpis, s katerim se javno objavi, da je bil glede nepremičnine začet zemljiškoknjižni postopek, v katerem sodišče o vpisu še ni odločilo. Plomba pomeni, da se pravno stanje lahko še spremeni — pred nakupom preverite, kaj je predmet postopka.',
                  },
                  {
                    term: 'Hipoteka',
                    definition:
                      'Zastavna pravica na nepremičnini, s katero je zavarovana terjatev — najpogosteje bančni kredit. Hipoteka zavaruje terjatev vse do njenega dokončnega poplačila; kupec se mora dogovoriti, kako bo izbrisana ob prenosu lastništva.',
                  },
                  {
                    term: 'Služnost',
                    definition:
                      'Pravica uporabljati tujo stvar ali zahtevati od lastnika, da opušča določena dejanja. Stvarna služnost pripada lastniku druge (gospodujoče) nepremičnine — na primer pravica poti čez parcelo; osebna služnost pripada določeni osebi in traja najdlje do njene smrti.',
                  },
                  {
                    term: 'Prepoved odtujitve in obremenitve',
                    definition:
                      'Omejitev, zaradi katere lastnik nepremičnine brez soglasja imetnika prepovedi ne more prodati ali obremeniti. V zemljiško knjigo se lahko vpiše samo, če je določena med zakoncema ali zunajzakonskima partnerjema, starši in otroki ali posvojitelji in posvojenci.',
                  },
                  {
                    term: 'Predkupna pravica',
                    definition:
                      'Pravica, da ima imetnik pri prodaji nepremičnine prednost pred drugimi kupci. Po zakonu jo imajo na primer solastniki pri prodaji solastniškega deleža; na nekaterih območjih jo ima občina, kar pokaže lokacijska informacija.',
                  },
                  {
                    term: 'Zemljiškoknjižno dovolilo',
                    alternateName: 'intabulacijska klavzula',
                    definition:
                      'Izrecna nepogojna izjava tistega, čigar pravica se prenaša, spreminja, obremenjuje ali preneha, da dovoljuje vpis v zemljiško knjigo. Podpis na njem mora biti overjen. Za vpis lastninske pravice je treba priložiti tudi potrdilo davčnega organa o plačilu davka na promet nepremičnin, kadar se ta plača.',
                  },
                ],
              },
              {
                sources: [
                  { label: 'Zakon o zemljiški knjigi (ZZK-1) — PISRS', href: 'https://pisrs.si/pregledPredpisa?id=ZAKO3603' },
                  { label: 'Stvarnopravni zakonik (SPZ) — PISRS', href: 'https://pisrs.si/pregledPredpisa?id=ZAKO3242' },
                  { label: 'Zemljiška knjiga — javni vpogled (e-Sodstvo)', href: 'https://esodisce.si/' },
                ],
              },
            ],
          },
          {
            h2: 'Vrednost in trg',
            blocks: [
              {
                terms: [
                  {
                    term: 'Množično vrednotenje nepremičnin',
                    definition:
                      'Postopek, v katerem GURS z modeli vrednotenja hkrati oceni vrednost vseh evidentiranih nepremičnin. Modeli se oblikujejo iz tržnih podatkov o cenah in najemninah, zbranih v evidenci trga nepremičnin (ETN).',
                  },
                  {
                    term: 'Posplošena vrednost',
                    alternateName: 'posplošena tržna vrednost',
                    definition:
                      'Modelska ocena tržne vrednosti nepremičnine, ki jo GURS izračuna ob upoštevanju temeljnih lastnosti (raba, lokacija, velikost, starost in kakovost). Trenutne posplošene vrednosti odražajo stanje trga na dan 1. 1. 2025 in se ob vsaki spremembi podatkov nepremičnine ponovno izračunajo. Ne upošteva stanja konkretne enote, zato ni cenitev.',
                  },
                  {
                    term: 'Realizirana cena',
                    definition:
                      'Cena, dosežena v dejansko sklenjenem poslu, kot je evidentirana v ETN. Za razliko od oglaševane cene, ki pove, koliko prodajalec zahteva, realizirana cena pove, koliko je kupec plačal.',
                  },
                ],
              },
              {
                sources: [
                  { label: 'GURS — posplošene vrednosti (e-prostor)', href: 'https://www.e-prostor.gov.si/podrocja/trg-in-vrednosti-nepremicnin/posplosene-vrednosti/' },
                  { label: 'Javni vpogled v vrednosti nepremičnin', href: 'https://vrednotenje.gov.si/EV_JV/' },
                ],
              },
            ],
          },
          {
            h2: 'Gradnja, raba in energija',
            blocks: [
              {
                terms: [
                  {
                    term: 'Namenska in dejanska raba',
                    definition:
                      'Namenska raba zemljišča je raba, ki jo določi občina (npr. stavbno ali kmetijsko zemljišče) in pove, kaj se na zemljišču sme; dejanska raba opisuje, kako se zemljišče ali del stavbe dejansko uporablja. Kataster nepremičnin podatke o namenski rabi prevzema od občin.',
                  },
                  {
                    term: 'Uporabno dovoljenje',
                    definition:
                      'Upravna odločba, na podlagi katere se objekt, za katerega je predpisano gradbeno dovoljenje (razen nezahtevnega objekta), lahko začne uporabljati. Kjer je predpisano, je tudi pogoj za določitev hišne številke.',
                  },
                  {
                    term: 'Energetska izkaznica',
                    definition:
                      'Javna listina s podatki o energetski učinkovitosti stavbe ali dela stavbe. Velja deset let; lastnik jo mora pri prodaji ali oddaji v najem kupcu oz. najemniku predložiti najpozneje pred sklenitvijo pogodbe.',
                  },
                  {
                    term: 'Energijski razred',
                    definition:
                      'Razvrstitev potrebne toplote za ogrevanje stavbe (kWh na m² na leto) v razrede od A1 do G. Razred A1 pomeni največ 10 kWh/(m²a), razred G več kot 210 kWh/(m²a).',
                  },
                ],
              },
              {
                sources: [
                  { label: 'GURS — namenska in dejanska raba v katastru (e-prostor)', href: 'https://www.e-prostor.gov.si/podrocja/parcele-in-stavbe/kataster-nepremicnin/' },
                  { label: 'eUprava — uporabno dovoljenje', href: 'https://e-uprava.gov.si/si/podrocja/nepremicnine-in-okolje/nepremicnine-stavbe/uporabno-dovoljenje.html' },
                  { label: 'Portal Energetika — energetska izkaznica stavbe', href: 'https://www.energetika-portal.si/podrocja/energetika/energetske-izkaznice-stavb/za-drzavljane/' },
                  { label: 'Pravilnik o metodologiji izdelave in izdaji energetskih izkaznic stavb (Uradni list RS, št. 4/23)', href: 'https://www.uradni-list.si/glasilo-uradni-list-rs/vsebina/2023-01-0094/pravilnik-o-metodologiji-izdelave-in-izdaji-energetskih-izkaznic-stavb' },
                ],
              },
            ],
          },
          {
            h2: 'Davki in dajatve',
            blocks: [
              {
                terms: [
                  {
                    term: 'Davek na promet nepremičnin',
                    definition:
                      'Davek po stopnji 2 % od prodajne cene, ki se plača pri prodaji nepremičnine, kadar se ne obračuna DDV. Zavezanec je prodajalec, tudi če plačilo s pogodbo prevzame kupec.',
                  },
                  {
                    term: 'Dobiček iz kapitala pri odsvojitvi nepremičnine',
                    definition:
                      'Dohodnina od razlike med vrednostjo nepremičnine ob prodaji in ob pridobitvi, ki jo plača fizična oseba. Stopnja je 25 % in se z vsakimi dopolnjenimi petimi leti lastništva zniža; po 15 letih se davek ne plača.',
                  },
                  {
                    term: 'NUSZ',
                    alternateName: 'nadomestilo za uporabo stavbnega zemljišča',
                    definition:
                      'Letna dajatev, katere območja, merila in oprostitve določi občina z odlokom, odmeri pa jo finančna uprava. Zavezanec je neposredni uporabnik stavbe ali zemljišča — lastnik ali najemnik.',
                  },
                  {
                    term: 'Davek od premoženja',
                    definition:
                      'Letni davek po Zakonu o davkih občanov, ki ga plačujejo fizične osebe, lastniki ali uživalci stavb, delov stavb, stanovanj, garaž in prostorov za počitek oziroma rekreacijo. Osnova se zniža za vrednost 160 m² stanovanjske površine, če je lastnik ali ožji družinski član v preteklem letu v njej stalno bival.',
                  },
                ],
              },
              {
                sources: [
                  { label: 'FURS — Brošura o obdavčitvi nepremičnin (8. izdaja, september 2026)', href: 'https://www.fu.gov.si/fileadmin/Internet/Davki_in_druge_dajatve/Podrocja/Davek_na_promet_nepremicnin/Opis/Brosura_o_obdavcitvi_nepremicnin.docx' },
                  { label: 'FURS — davek na promet nepremičnin', href: 'https://www.fu.gov.si/davki_in_druge_dajatve/podrocja/davek_na_promet_nepremicnin/' },
                ],
              },
            ],
          },
        ],
        faq: [
          {
            question: 'Kakšna je razlika med katastrom nepremičnin in zemljiško knjigo?',
            answer:
              'Kataster nepremičnin (vodi ga GURS) opisuje, kaj nepremičnina je: parcele, stavbe, dele stavb, njihovo lego in površino. Zemljiška knjiga (vodijo jo okrajna sodišča) pove, kdo ima na nepremičnini kakšne pravice — lastništvo, hipoteke, služnosti. Pred nakupom preverite obe.',
          },
          {
            question: 'Zakaj ima stanovanje dve različni površini?',
            answer:
              'Neto tlorisna površina zajema vse prostore, ki pripadajo stanovanju, tudi klet in balkon. Uporabna površina zajema le prostore, ki se uporabljajo za bivanje. Ko primerjate cene na m², preverite, na katero površino se nanašajo.',
          },
          {
            question: 'Kaj pomeni plomba v zemljiškoknjižnem izpisku?',
            answer:
              'Da je bil glede nepremičnine vložen predlog za vpis, o katerem sodišče še ni odločilo. Stanje v zemljiški knjigi se lahko še spremeni, zato pred podpisom pogodbe preverite, kaj je predmet postopka.',
          },
        ],
      },
      en: {
        path: '/en/guide/slovenian-property-glossary',
        title: 'Slovenian Property Glossary (2026) | nReport',
        description:
          'The Slovenian property terms you meet when buying: GURS, ETN, the land register, the cadastre, the generalized value, pending entries, easements and more.',
        breadcrumb: 'Property glossary',
        eyebrow: 'Concepts',
        h1: 'Slovenian Property Glossary',
        intro:
          'Buying property in Slovenia brings you into three official worlds: the real estate cadastre (what the property physically is), the land register (who holds which rights over it) and the market and valuation records (what it is worth). This glossary explains their terms in one place. Terms are given in Slovenian, as they appear in registers, contracts and reports, and each definition rests on the law or official page listed below its section.',
        sections: [
          {
            h2: 'Official registers and institutions',
            blocks: [
              {
                terms: [
                  {
                    term: 'GURS',
                    alternateName: 'Surveying and Mapping Authority of the Republic of Slovenia',
                    definition:
                      'The state authority that keeps the real estate cadastre, the real estate market register (ETN) and the valuation register with generalized values. Its data are publicly available through the Prostor portal (e-prostor.gov.si).',
                  },
                  {
                    term: 'Kataster nepremičnin',
                    alternateName: 'real estate cadastre',
                    definition:
                      'The official record of parcels, buildings and building parts — their position, shape, area and other physical characteristics. It records the actual state of property and enables rights to be entered in the land register. Under the Real Estate Cadastre Act (ZKN) it replaced three earlier records: the land cadastre, the building cadastre and the real estate register; its information system has been in use since 4 April 2022.',
                  },
                  {
                    term: 'Zemljiška knjiga',
                    alternateName: 'land register',
                    definition:
                      'A public register for entering and publishing rights over property (ownership, mortgage, easement, building right …) and legal facts relating to it. It is kept by the local courts, and every entry is public. Anyone who acts in good faith and relies on the registered data must not suffer harm as a result.',
                  },
                  {
                    term: 'ETN',
                    alternateName: 'evidenca trga nepremičnin, real estate market register',
                    definition:
                      'The public register of property sales and rentals kept by GURS. It records the prices and rents achieved in concluded deals and data on the property concerned; rentals are recorded if concluded for at least six months. The parties’ personal data are not public.',
                  },
                ],
              },
              {
                sources: [
                  { label: 'GURS — real estate cadastre (e-prostor, in Slovenian)', href: 'https://www.e-prostor.gov.si/podrocja/parcele-in-stavbe/kataster-nepremicnin/' },
                  { label: 'Real Estate Cadastre Act (ZKN), Official Gazette 54/21', href: 'https://www.uradni-list.si/glasilo-uradni-list-rs/vsebina/2021-01-1047' },
                  { label: 'Slovenian judiciary — land register', href: 'https://www.sodisce.si/javne_knjige/zemljiska_knjiga/' },
                  { label: 'GURS — property market and ETN (e-prostor, in Slovenian)', href: 'https://www.e-prostor.gov.si/podrocja/trg-in-vrednosti-nepremicnin/trg-nepremicnin/' },
                ],
              },
            ],
          },
          {
            h2: 'Property, parcels and floor area',
            blocks: [
              {
                terms: [
                  {
                    term: 'Katastrska občina',
                    alternateName: 'cadastral municipality (KO)',
                    definition:
                      'The basic unit in which cadastre data are kept. Every parcel and building in Slovenia is uniquely identified by the cadastral municipality code and a number within it. A cadastral municipality is not the same as a municipality.',
                  },
                  {
                    term: 'Parcela',
                    alternateName: 'parcel, parcel number',
                    definition:
                      'A surveyed piece of land lying in one cadastral municipality, entered in the cadastre with its boundary and marked with a parcel number. A parcel number is written e.g. “parcela 123 24/2”: the cadastral municipality code, the parcel number and, where set, a sub-number after the slash.',
                  },
                  {
                    term: 'Stavba',
                    alternateName: 'building',
                    definition:
                      'A covered structure that can be entered and is intended for living or carrying out an activity. Every building has at least one building part.',
                  },
                  {
                    term: 'Del stavbe',
                    alternateName: 'building part',
                    definition:
                      'A functional set of rooms in a building suitable for independent use — for example an apartment or an office; in a building under condominium ownership it is the individual unit. Its number combines the building number and a number assigned within the building.',
                  },
                  {
                    term: 'Neto tlorisna površina dela stavbe',
                    alternateName: 'net floor area',
                    definition:
                      'The sum of the areas of all rooms belonging to a building part. For an apartment it includes its ancillary rooms outside the living space, such as a cellar on another floor. This is the area the cadastre records for a building part; nReport uses it as the basis for price per m².',
                  },
                  {
                    term: 'Uporabna površina dela stavbe',
                    alternateName: 'usable area',
                    definition:
                      'The sum of the areas of rooms used for the same purpose as the building part. For an apartment it counts the living rooms but not a cellar, balcony, terrace, loggia or garage — so it is smaller than the net floor area. A price per m² of usable area is not directly comparable with one per m² of net floor area.',
                  },
                  {
                    term: 'Etažna lastnina',
                    alternateName: 'condominium ownership',
                    definition:
                      'Ownership of an individual unit of a building (e.g. an apartment) together with co-ownership of the building’s common parts. It is disposed of as a whole: selling the apartment also sells its share of the common parts.',
                  },
                  {
                    term: 'Rezervni sklad',
                    alternateName: 'reserve fund',
                    definition:
                      'The owners’ fund for the cost of maintenance and necessary improvements to the building. It is the joint property of the unit owners, kept by the building manager on a separate account, and an owner cannot ask for contributions back — not even when selling the apartment.',
                  },
                ],
              },
              {
                sources: [
                  { label: 'GURS — cadastre: parcels, buildings, building parts (e-prostor, in Slovenian)', href: 'https://www.e-prostor.gov.si/podrocja/parcele-in-stavbe/kataster-nepremicnin/' },
                  { label: 'Cadastre data rules, Annex 3 — measuring rooms and usable area (in Slovenian)', href: 'https://www.uradni-list.si/files/RS_-2022-041-00786-OB~P006-0000.PDF' },
                  { label: 'Property Code (SPZ) — PISRS', href: 'https://pisrs.si/pregledPredpisa?id=ZAKO3242' },
                ],
              },
            ],
          },
          {
            h2: 'Rights and encumbrances in the land register',
            blocks: [
              {
                terms: [
                  {
                    term: 'Vknjižba, predznamba in zaznamba',
                    alternateName: 'registration, pre-registration and notation',
                    definition:
                      'The main land-register entries. Registration (vknjižba) achieves or records the acquisition or termination of a right; pre-registration (predznamba) does the same on condition that it is later justified; notation (zaznamba) records legal facts such as a dispute or an enforcement.',
                  },
                  {
                    term: 'Plomba',
                    alternateName: 'pending-entry marker',
                    definition:
                      'An auxiliary entry announcing that land-register proceedings concerning the property have started and the court has not yet decided on the entry. It means the legal status may still change — check what the proceedings concern before buying.',
                  },
                  {
                    term: 'Hipoteka',
                    alternateName: 'mortgage',
                    definition:
                      'A pledge over real property securing a claim — most often a bank loan. It secures the claim until it is fully repaid; a buyer needs an agreement on how it will be deleted when ownership transfers.',
                  },
                  {
                    term: 'Služnost',
                    alternateName: 'easement, servitude',
                    definition:
                      'A right to use someone else’s property or to require its owner to refrain from certain acts. A real easement belongs to the owner of another (dominant) property — for example a right of way across a parcel; a personal easement belongs to a specific person and lasts at most until their death.',
                  },
                  {
                    term: 'Prepoved odtujitve in obremenitve',
                    alternateName: 'prohibition of disposal and encumbrance',
                    definition:
                      'A restriction under which the owner cannot sell or encumber the property without the consent of the holder of the prohibition. It can be entered in the land register only between spouses or unmarried partners, parents and children, or adoptive parents and adopted children.',
                  },
                  {
                    term: 'Predkupna pravica',
                    alternateName: 'right of pre-emption',
                    definition:
                      'The right to be preferred over other buyers when a property is sold. By law it belongs, for example, to co-owners when a co-ownership share is sold; in some areas the municipality holds it, which the location information (lokacijska informacija) shows.',
                  },
                  {
                    term: 'Zemljiškoknjižno dovolilo',
                    alternateName: 'intabulacijska klavzula, registration consent',
                    definition:
                      'An express, unconditional statement by the person whose right is transferred, changed, encumbered or terminated, allowing the entry in the land register. The signature on it must be certified. To register ownership, the tax office’s confirmation that the real estate transfer tax was paid must also be attached where that tax is due.',
                  },
                ],
              },
              {
                sources: [
                  { label: 'Land Register Act (ZZK-1) — PISRS', href: 'https://pisrs.si/pregledPredpisa?id=ZAKO3603' },
                  { label: 'Property Code (SPZ) — PISRS', href: 'https://pisrs.si/pregledPredpisa?id=ZAKO3242' },
                  { label: 'Land register — public access (e-Justice)', href: 'https://esodisce.si/' },
                ],
              },
            ],
          },
          {
            h2: 'Value and the market',
            blocks: [
              {
                terms: [
                  {
                    term: 'Množično vrednotenje nepremičnin',
                    alternateName: 'mass real estate valuation',
                    definition:
                      'The procedure in which GURS values all registered properties at once using valuation models. The models are built from market data on prices and rents collected in the real estate market register (ETN).',
                  },
                  {
                    term: 'Posplošena vrednost',
                    alternateName: 'generalized (market) value',
                    definition:
                      'A model estimate of a property’s market value that GURS calculates from its basic characteristics (use, location, size, age and quality). Current generalized values reflect the market as of 1 January 2025 and are recalculated whenever the property’s data change. It ignores the condition of the specific unit, so it is not an appraisal.',
                  },
                  {
                    term: 'Realizirana cena',
                    alternateName: 'realized price',
                    definition:
                      'The price achieved in an actually concluded deal, as recorded in ETN. Unlike an asking price, which says what the seller wants, a realized price says what a buyer paid.',
                  },
                ],
              },
              {
                sources: [
                  { label: 'GURS — generalized values (e-prostor, in Slovenian)', href: 'https://www.e-prostor.gov.si/podrocja/trg-in-vrednosti-nepremicnin/posplosene-vrednosti/' },
                  { label: 'Public view of property values', href: 'https://vrednotenje.gov.si/EV_JV/' },
                ],
              },
            ],
          },
          {
            h2: 'Building, land use and energy',
            blocks: [
              {
                terms: [
                  {
                    term: 'Namenska in dejanska raba',
                    alternateName: 'designated and actual use',
                    definition:
                      'Designated use of land is set by the municipality (e.g. building or agricultural land) and says what the land may be used for; actual use describes how the land or building part is really used. The cadastre takes designated-use data from the municipalities.',
                  },
                  {
                    term: 'Uporabno dovoljenje',
                    alternateName: 'occupancy permit',
                    definition:
                      'The administrative decision under which a structure that requires a building permit (other than a minor structure) may start to be used. Where it is required, it is also a condition for being assigned a house number.',
                  },
                  {
                    term: 'Energetska izkaznica',
                    alternateName: 'energy performance certificate',
                    definition:
                      'A public document with data on the energy performance of a building or building part. It is valid for ten years; when selling or letting, the owner must present it to the buyer or tenant no later than before the contract is concluded.',
                  },
                  {
                    term: 'Energijski razred',
                    alternateName: 'energy class',
                    definition:
                      'The banding of a building’s heating energy need (kWh per m² per year) into classes A1 to G. Class A1 means at most 10 kWh/(m²a); class G means more than 210 kWh/(m²a).',
                  },
                ],
              },
              {
                sources: [
                  { label: 'GURS — designated and actual use in the cadastre (e-prostor, in Slovenian)', href: 'https://www.e-prostor.gov.si/podrocja/parcele-in-stavbe/kataster-nepremicnin/' },
                  { label: 'eUprava — occupancy permit (in Slovenian)', href: 'https://e-uprava.gov.si/si/podrocja/nepremicnine-in-okolje/nepremicnine-stavbe/uporabno-dovoljenje.html' },
                  { label: 'Energy portal — building energy certificate (in Slovenian)', href: 'https://www.energetika-portal.si/podrocja/energetika/energetske-izkaznice-stavb/za-drzavljane/' },
                  { label: 'Rules on energy certificates, Official Gazette 4/23 (in Slovenian)', href: 'https://www.uradni-list.si/glasilo-uradni-list-rs/vsebina/2023-01-0094/pravilnik-o-metodologiji-izdelave-in-izdaji-energetskih-izkaznic-stavb' },
                ],
              },
            ],
          },
          {
            h2: 'Taxes and charges',
            blocks: [
              {
                terms: [
                  {
                    term: 'Davek na promet nepremičnin',
                    alternateName: 'real estate transfer tax',
                    definition:
                      'A 2 % tax on the sale price, due when property is sold without VAT being charged. The taxpayer is the seller, even if the buyer takes over payment in the contract.',
                  },
                  {
                    term: 'Dobiček iz kapitala pri odsvojitvi nepremičnine',
                    alternateName: 'capital gains tax on property',
                    definition:
                      'Personal income tax on the difference between a property’s value at sale and at acquisition, paid by individuals. The rate is 25 % and falls with every full five years of ownership; after 15 years no tax is due.',
                  },
                  {
                    term: 'NUSZ',
                    alternateName: 'nadomestilo za uporabo stavbnega zemljišča, building land use charge',
                    definition:
                      'An annual charge whose areas, criteria and exemptions each municipality sets by ordinance, assessed by the Financial Administration. The payer is the direct user of the building or land — the owner or a tenant.',
                  },
                  {
                    term: 'Davek od premoženja',
                    alternateName: 'property tax on buildings',
                    definition:
                      'An annual tax under the Citizens’ Taxes Act paid by individuals who own or hold buildings, building parts, apartments, garages and holiday premises. Its base is reduced by the value of 160 m² of living space if the owner or a close family member lived there permanently in the previous year.',
                  },
                ],
              },
              {
                sources: [
                  { label: 'FURS — brochure on property taxation (8th edition, September 2026, in Slovenian)', href: 'https://www.fu.gov.si/fileadmin/Internet/Davki_in_druge_dajatve/Podrocja/Davek_na_promet_nepremicnin/Opis/Brosura_o_obdavcitvi_nepremicnin.docx' },
                  { label: 'FURS — real estate transfer tax (in Slovenian)', href: 'https://www.fu.gov.si/davki_in_druge_dajatve/podrocja/davek_na_promet_nepremicnin/' },
                ],
              },
            ],
          },
        ],
        faq: [
          {
            question: 'What is the difference between the cadastre and the land register?',
            answer:
              'The real estate cadastre (kept by GURS) describes what the property is: parcels, buildings, building parts, their location and area. The land register (kept by the local courts) says who holds which rights over it — ownership, mortgages, easements. Check both before buying.',
          },
          {
            question: 'Why does an apartment have two different floor areas?',
            answer:
              'The net floor area covers every room belonging to the apartment, including a cellar and balcony. The usable area covers only the rooms used for living. When comparing prices per m², check which area they refer to.',
          },
          {
            question: 'What does a “plomba” on a land-register extract mean?',
            answer:
              'That an application for an entry concerning the property has been filed and the court has not yet decided on it. The land-register status may still change, so check what the proceedings concern before signing a contract.',
          },
        ],
      },
    },
  },

  // --------------------------------------------------------- energy certificate
  // Law: Zakon o učinkoviti rabi energije (ZURE, Uradni list RS, št. 158/20),
  // arts. 30–31 and the penalty provisions as summarised on the Energy portal;
  // class bands from the Pravilnik (Uradni list RS, št. 4/23). The recast EPBD
  // (2024/1275) is being transposed through a new ZURE-1 that was still in
  // preparation at the time of writing — re-check both when updating.
  {
    group: 'energy-certificate',
    datePublished: '2026-09-28',
    dateModified: '2026-09-28',
    changefreq: 'monthly',
    priority: '0.7',
    locales: {
      sl: {
        path: '/vodnik/energetska-izkaznica',
        title: 'Energetska izkaznica: kdaj je obvezna (2026) | nReport',
        description:
          'Kdaj je energetska izkaznica obvezna pri prodaji in najemu, kaj pomenijo energijski razredi A1–G, koliko časa velja, kako jo preverite in kakšne so globe.',
        breadcrumb: 'Energetska izkaznica',
        eyebrow: 'Nakup',
        h1: 'Energetska izkaznica stavbe: kaj morate vedeti',
        intro:
          'Energetska izkaznica je javna listina s podatki o energetski učinkovitosti stavbe ali stanovanja. Pri prodaji ali oddaji v najem jo mora lastnik kupcu oziroma najemniku predložiti najpozneje pred sklenitvijo pogodbe, njene energijske kazalnike pa navesti že v oglasu. Velja deset let. Energijski razred od A1 do G pove, koliko toplote stavba potrebuje za ogrevanje na kvadratni meter na leto.',
        sections: [
          {
            h2: 'Kdaj je energetska izkaznica obvezna?',
            blocks: [
              {
                p: 'Po Zakonu o učinkoviti rabi energije (ZURE) mora lastnik zagotoviti energetsko izkaznico za stavbe ali posamezne dele stavb, ki se zgradijo, prodajo ali oddajo. Obveznost velja v teh primerih:',
              },
              {
                ul: [
                  'pri prodaji stavbe ali stanovanja,',
                  'pri oddaji v nov najem, daljši od enega leta — tudi kadar zaporedne krajše pogodbe z istim najemnikom skupaj preidejo eno leto (prekinitev treh mesecev ali manj se ne šteje),',
                  'za nove stavbe,',
                  'za stavbe javnega sektorja s celotno uporabno površino nad 250 m².',
                ],
              },
              {
                p: 'Pri prodaji stanovanja se lahko namesto izkaznice za stanovanje predloži izkaznica za celotno stavbo. Za stavbo, v kateri ni vzpostavljena etažna lastnina, se izkaznica izda le za celotno stavbo. Lastniki, ki nepremičnine ne prodajajo in ne oddajajo, izkaznice ne potrebujejo.',
              },
              { h3: 'Kdaj je ni treba predložiti' },
              {
                ul: [
                  'pri oddaji v najem za manj kot eno leto,',
                  'pri prodaji v izvršilnem ali stečajnem postopku ali zaradi razlastitve,',
                  'pri prodaji nevzdrževanih, zapuščenih in dotrajanih stavb, neprimernih za uporabo, za katere izračun kazalnikov ni mogoč,',
                  'za stavbe za obredne ali verske namene, enostavne in nezahtevne objekte ter samostojne stavbe s celotno uporabno površino, manjšo od 50 m²,',
                  'za industrijske, skladiščne in nestanovanjske kmetijske stavbe, v katerih se energija ne uporablja za notranje klimatske pogoje.',
                ],
              },
            ],
          },
          {
            h2: 'Kaj pomeni energijski razred?',
            blocks: [
              {
                p: 'Razred se določi iz potrebne toplote za ogrevanje — koliko toplote za ogrevanje stavba potrebuje na kvadratni meter na leto. Meje razredov določa Pravilnik o metodologiji izdelave in izdaji energetskih izkaznic stavb:',
              },
              {
                table: {
                  head: ['Razred', 'Potrebna toplota za ogrevanje'],
                  rows: [
                    ['A1', 'od 0 do vključno 10 kWh/(m²a)'],
                    ['A2', 'nad 10 do vključno 15 kWh/(m²a)'],
                    ['B1', 'nad 15 do vključno 25 kWh/(m²a)'],
                    ['B2', 'nad 25 do vključno 35 kWh/(m²a)'],
                    ['C', 'nad 35 do vključno 60 kWh/(m²a)'],
                    ['D', 'nad 60 do vključno 105 kWh/(m²a)'],
                    ['E', 'nad 105 do vključno 150 kWh/(m²a)'],
                    ['F', 'nad 150 do vključno 210 kWh/(m²a)'],
                    ['G', 'nad 210 kWh/(m²a)'],
                  ],
                },
              },
              {
                p: 'Računska izkaznica — ta se izda za stanovanjske stavbe, stanovanja in novogradnje — poleg tega navaja dovedeno energijo za delovanje stavbnih sistemov, primarno energijo in emisije CO₂. Merjena izkaznica, ki temelji na dejanski porabi v zadnjih treh letih, se izda za obstoječe nestanovanjske stavbe. Razen pri novih stavbah so sestavni del izkaznice tudi priporočila za stroškovno učinkovite izboljšave.',
              },
              {
                callout: {
                  title: 'Razred ni račun',
                  text: 'Razred opisuje stavbo pri standardiziranih pogojih uporabe, ne vaše prihodnje položnice. Dejanska poraba je odvisna tudi od načina ogrevanja, cene energenta in navad stanovalcev.',
                },
              },
            ],
          },
          {
            h2: 'Kako preverite izkaznico pred nakupom',
            blocks: [
              {
                steps: [
                  { title: 'Zahtevajte izkaznico pred podpisom', text: 'Prodajalec vam jo mora predložiti v izvirniku ali kopiji najpozneje pred sklenitvijo pogodbe. Energijski kazalniki morajo biti navedeni že v oglasu.' },
                  { title: 'Preverite register', text: 'Register energetskih izkaznic vodi ministrstvo, pristojno za energijo. Podatek o izdani izkaznici za stavbo ali stanovanje najdete v javnem vpogledu v podatke GURS na prostorskem portalu.' },
                  { title: 'Preverite veljavnost', text: 'Izkaznica velja deset let od izdaje. Stavba ali stanovanje ima lahko le eno veljavno izkaznico — nova razveljavi prejšnjo.' },
                  { title: 'Preverite, na kaj se nanaša', text: 'Izkaznica za celotno stavbo pove povprečje stavbe, ne nujno stanja vašega stanovanja (lega, nadstropje, zamenjana okna).' },
                ],
              },
              {
                p: 'Poročilo nReport preveri register energetskih izkaznic za stanovanje in za stavbo ter navede, ali je izkaznica evidentirana, kdaj je bila izdana in do kdaj velja; potečeno izkaznico posebej označi. Če registra ni bilo mogoče preveriti, to zapiše, namesto da bi sklepal, da izkaznice ni.',
              },
            ],
          },
          {
            h2: 'Kakšne so globe?',
            blocks: [
              {
                p: 'Z globo 300 € se kaznuje lastnik stavbe ali dela stavbe, z globo 100 € pa odgovorna oseba pravne osebe ali podjetnika, če pri prodaji ali oddaji v najem kupcu oziroma najemniku pred sklenitvijo pogodbe ne predloži veljavne izkaznice ali če v oglasu ne zagotovi navedbe energijskih kazalnikov.',
              },
            ],
          },
          {
            h2: 'Kdo izdela izkaznico in ali se pravila spreminjajo?',
            blocks: [
              {
                p: 'Izkaznico izdelajo neodvisni strokovnjaki z licenco, izdajo pa jo pooblaščene osebe, ki jim pooblastilo podeli minister, pristojen za energijo. Vsako izdano izkaznico mora strokovnjak ob izdaji prijaviti za vpis v register.',
              },
              {
                p: 'Evropska unija je leta 2024 sprejela prenovljeno direktivo o energetski učinkovitosti stavb (EU) 2024/1275. Za prenos v slovenski pravni red je bil jeseni 2024 v javni obravnavi nov Zakon o učinkoviti rabi energije (ZURE-1), ki uvaja tudi nove zahteve glede energetskih izkaznic. Portal Energetika ob zadnji posodobitvi tega vodnika med veljavnimi predpisi še navaja ZURE (Uradni list RS, št. 158/20) in pravilnik iz leta 2023, zato ta vodnik opisuje ta pravila.',
              },
              {
                callout: {
                  title: 'Ni pravni nasvet',
                  text: 'Vodnik povzema veljavne predpise in ni pravni nasvet. Za konkreten primer preverite besedilo zakona ali se posvetujte s strokovnjakom.',
                },
              },
            ],
          },
          {
            h2: 'Viri',
            blocks: [
              {
                sources: [
                  { label: 'Portal Energetika — energetska izkaznica stavbe (informacije za državljane)', href: 'https://www.energetika-portal.si/podrocja/energetika/energetske-izkaznice-stavb/za-drzavljane/' },
                  { label: 'Zakon o učinkoviti rabi energije (ZURE), Uradni list RS, št. 158/20', href: 'https://www.uradni-list.si/glasilo-uradni-list-rs/vsebina/2020-01-2762' },
                  { label: 'Pravilnik o metodologiji izdelave in izdaji energetskih izkaznic stavb, Uradni list RS, št. 4/23', href: 'https://www.uradni-list.si/glasilo-uradni-list-rs/vsebina/2023-01-0094/pravilnik-o-metodologiji-izdelave-in-izdaji-energetskih-izkaznic-stavb' },
                  { label: 'Portal Energetika — register energetskih izkaznic', href: 'https://www.energetika-portal.si/podrocja/energetika/energetske-izkaznice-stavb/register-energetskih-izkaznic/' },
                  { label: 'Javni vpogled v nepremičninske podatke (GURS)', href: 'https://ipi.eprostor.gov.si/jv/' },
                  { label: 'Portal Energetika — javna obravnava ZURE-1 (2024)', href: 'https://www.energetika-portal.si/nc/novica/n/javna-obravnava-novega-zakona-o-ucinkoviti-rabi-energije-zure-1/' },
                ],
              },
            ],
          },
        ],
        faq: [
          {
            question: 'Ali potrebujem energetsko izkaznico, če stanovanje oddam za pol leta?',
            answer:
              'Ne, pri oddaji za manj kot eno leto je ni treba predložiti. Če pa z istim najemnikom sklenete zaporedne pogodbe, ki skupaj trajajo več kot eno leto, jo morate predložiti ob pogodbi, s katero najem preseže eno leto.',
          },
          {
            question: 'Koliko časa velja energetska izkaznica?',
            answer:
              'Deset let od izdaje. Novo izkaznico lahko pridobite tudi prej; ta razveljavi prejšnjo.',
          },
          {
            question: 'Ali lahko prodajalec namesto izkaznice za stanovanje pokaže izkaznico za celo stavbo?',
            answer:
              'Da, zakon to dovoljuje. Upoštevajte, da izkaznica za stavbo opisuje stavbo kot celoto in ne nujno stanja posameznega stanovanja.',
          },
          {
            question: 'Kje preverim, ali ima stavba izkaznico?',
            answer:
              'Podatek o izdani izkaznici za stavbo ali del stavbe je dostopen v javnem vpogledu v podatke GURS na prostorskem portalu; register vodi ministrstvo, pristojno za energijo.',
          },
        ],
      },
      en: {
        path: '/en/guide/energy-performance-certificate-slovenia',
        title: 'Energy Certificate in Slovenia (2026) | nReport',
        description:
          'When an energy performance certificate is mandatory in Slovenia, what classes A1–G mean, how long it is valid, how to check it before buying and the fines.',
        breadcrumb: 'Energy certificate',
        eyebrow: 'Buying',
        h1: 'The Energy Performance Certificate in Slovenia',
        intro:
          'An energy performance certificate (energetska izkaznica) is a public document with data on the energy performance of a building or apartment. When selling or letting, the owner must present it to the buyer or tenant no later than before the contract is concluded, and state its energy indicators in the listing. It is valid for ten years. The energy class, from A1 to G, says how much heat the building needs for heating per square metre per year.',
        sections: [
          {
            h2: 'When is an energy certificate mandatory?',
            blocks: [
              {
                p: 'Under the Efficient Use of Energy Act (ZURE), owners must provide a certificate for buildings or building parts that are built, sold or let. The obligation applies:',
              },
              {
                ul: [
                  'when selling a building or apartment,',
                  'when letting under a new lease longer than one year — including when consecutive shorter leases with the same tenant together exceed a year (a break of three months or less does not count),',
                  'for new buildings,',
                  'for public-sector buildings with a total useful floor area over 250 m².',
                ],
              },
              {
                p: 'When an apartment is sold, a certificate for the whole building may be presented instead of one for the apartment. For a building without condominium ownership, a certificate can only be issued for the whole building. Owners who are not selling or letting do not need one.',
              },
              { h3: 'When it does not have to be presented' },
              {
                ul: [
                  'leases shorter than one year,',
                  'sales in enforcement or bankruptcy proceedings or for expropriation,',
                  'sales of neglected, abandoned and dilapidated buildings unfit for use, for which the indicators cannot be calculated,',
                  'buildings used for worship, simple and minor structures, and stand-alone buildings with a total useful floor area under 50 m²,',
                  'industrial, storage and non-residential agricultural buildings in which no energy is used to condition the indoor climate.',
                ],
              },
            ],
          },
          {
            h2: 'What does the energy class mean?',
            blocks: [
              {
                p: 'The class is based on the heating energy need — how much heat for heating the building needs per square metre per year. The class bands are set by the Rules on the methodology for energy certificates:',
              },
              {
                table: {
                  head: ['Class', 'Heating energy need'],
                  rows: [
                    ['A1', '0 to 10 kWh/(m²a) inclusive'],
                    ['A2', 'over 10 to 15 kWh/(m²a)'],
                    ['B1', 'over 15 to 25 kWh/(m²a)'],
                    ['B2', 'over 25 to 35 kWh/(m²a)'],
                    ['C', 'over 35 to 60 kWh/(m²a)'],
                    ['D', 'over 60 to 105 kWh/(m²a)'],
                    ['E', 'over 105 to 150 kWh/(m²a)'],
                    ['F', 'over 150 to 210 kWh/(m²a)'],
                    ['G', 'over 210 kWh/(m²a)'],
                  ],
                },
              },
              {
                p: 'A calculated certificate — issued for residential buildings, apartments and new buildings — also states the delivered energy for building systems, primary energy and CO₂ emissions. A measured certificate, based on actual consumption over the last three years, is issued for existing non-residential buildings. Except for new buildings, a certificate also includes recommendations for cost-effective improvements.',
              },
              {
                callout: {
                  title: 'A class is not a bill',
                  text: 'The class describes the building under standardised conditions of use, not your future energy bills. Actual consumption also depends on the heating system, energy prices and how the occupants live.',
                },
              },
            ],
          },
          {
            h2: 'How to check the certificate before buying',
            blocks: [
              {
                steps: [
                  { title: 'Ask for it before signing', text: 'The seller must present the original or a copy no later than before the contract is concluded. The energy indicators must already appear in the listing.' },
                  { title: 'Check the register', text: 'The register of energy certificates is kept by the ministry responsible for energy. Whether a certificate has been issued for a building or apartment can be seen in the public view of GURS data on the spatial portal.' },
                  { title: 'Check validity', text: 'A certificate is valid for ten years from issue. A building or apartment can have only one valid certificate — a new one cancels the previous one.' },
                  { title: 'Check what it covers', text: 'A certificate for the whole building gives the building’s average, not necessarily the state of your apartment (position, floor, replaced windows).' },
                ],
              },
              {
                p: 'An nReport report checks the energy-certificate register for both the apartment and the building and states whether a certificate is recorded, when it was issued and until when it is valid; an expired certificate is flagged. If the register could not be checked, the report says so rather than concluding there is no certificate.',
              },
            ],
          },
          {
            h2: 'What are the fines?',
            blocks: [
              {
                p: 'The owner of a building or building part is fined €300, and the responsible person of a company or sole trader €100, for failing to present a valid certificate to the buyer or tenant before the contract is concluded, or for failing to ensure that the energy indicators are stated in the listing.',
              },
            ],
          },
          {
            h2: 'Who issues certificates, and are the rules changing?',
            blocks: [
              {
                p: 'Certificates are prepared by licensed independent experts and issued by authorised issuers appointed by the minister responsible for energy. Each certificate must be reported for entry in the register when it is issued.',
              },
              {
                p: 'In 2024 the EU adopted the recast Energy Performance of Buildings Directive (EU) 2024/1275. To transpose it, a new Efficient Use of Energy Act (ZURE-1), which also brings new requirements for energy certificates, went to public consultation in autumn 2024. At the last update of this guide the Energy portal still lists ZURE (Official Gazette 158/20) and the 2023 rules as the legislation in force, so this guide describes those rules.',
              },
              {
                callout: {
                  title: 'Not legal advice',
                  text: 'This guide summarises the rules in force and is not legal advice. For a specific case, check the text of the law or consult a professional.',
                },
              },
            ],
          },
          {
            h2: 'Sources',
            blocks: [
              {
                sources: [
                  { label: 'Energy portal — building energy certificate (in Slovenian)', href: 'https://www.energetika-portal.si/podrocja/energetika/energetske-izkaznice-stavb/za-drzavljane/' },
                  { label: 'Efficient Use of Energy Act (ZURE), Official Gazette 158/20 (in Slovenian)', href: 'https://www.uradni-list.si/glasilo-uradni-list-rs/vsebina/2020-01-2762' },
                  { label: 'Rules on energy certificates, Official Gazette 4/23 (in Slovenian)', href: 'https://www.uradni-list.si/glasilo-uradni-list-rs/vsebina/2023-01-0094/pravilnik-o-metodologiji-izdelave-in-izdaji-energetskih-izkaznic-stavb' },
                  { label: 'Energy portal — register of energy certificates (in Slovenian)', href: 'https://www.energetika-portal.si/podrocja/energetika/energetske-izkaznice-stavb/register-energetskih-izkaznic/' },
                  { label: 'Public view of property data (GURS)', href: 'https://ipi.eprostor.gov.si/jv/' },
                  { label: 'Energy portal — ZURE-1 public consultation, 2024 (in Slovenian)', href: 'https://www.energetika-portal.si/nc/novica/n/javna-obravnava-novega-zakona-o-ucinkoviti-rabi-energije-zure-1/' },
                ],
              },
            ],
          },
        ],
        faq: [
          {
            question: 'Do I need a certificate to let my apartment for six months?',
            answer:
              'No, leases shorter than one year are exempt. But if you sign consecutive leases with the same tenant that together last more than a year, you must present one with the lease that takes the tenancy past a year.',
          },
          {
            question: 'How long is an energy certificate valid?',
            answer:
              'Ten years from issue. You can obtain a new one earlier; it cancels the previous one.',
          },
          {
            question: 'Can the seller show a certificate for the whole building instead of the apartment?',
            answer:
              'Yes, the law allows it. Bear in mind that a building certificate describes the building as a whole and not necessarily the state of an individual apartment.',
          },
          {
            question: 'Where can I check whether a building has a certificate?',
            answer:
              'Whether a certificate has been issued for a building or building part can be seen in the public view of GURS data on the spatial portal; the register is kept by the ministry responsible for energy.',
          },
        ],
      },
    },
  },

  // ------------------------------------------------------------ property taxes
  // Every rate and deadline below is from FURS: the "Brošura o obdavčitvi
  // nepremičnin" (8th edition, September 2026), the DPN description, the VAT
  // rates document (14th edition, June 2026), the rental-income description and
  // the eDavki form page — plus the Constitutional Court decision U-I-313/13.
  // Tax rules change; re-check the FURS brochure edition when updating.
  {
    group: 'property-taxes',
    datePublished: '2026-09-28',
    dateModified: '2026-09-28',
    changefreq: 'monthly',
    priority: '0.8',
    locales: {
      sl: {
        path: '/vodnik/nepremicninski-davki',
        title: 'Nepremičninski davki v Sloveniji (2026) | nReport',
        description:
          'Kateri davki se plačajo pri nakupu, lastništvu in prodaji nepremičnine v Sloveniji: davek na promet (2 %), DDV, dobiček iz kapitala, NUSZ, davek od premoženja.',
        breadcrumb: 'Nepremičninski davki',
        eyebrow: 'Davki',
        h1: 'Nepremičninski davki v Sloveniji',
        intro:
          'Pri nakupu rabljene nepremičnine v Sloveniji se plača 2-odstotni davek na promet nepremičnin, ki ga po zakonu dolguje prodajalec; kadar pa prodajalec obračuna DDV — praviloma pri novogradnjah —, se davek na promet ne plača. Lastnik nato letno plačuje nadomestilo za uporabo stavbnega zemljišča (NUSZ) in davek od premoženja, pri prodaji pa se lahko obdavči dobiček: po stopnji 25 %, ki se z leti lastništva znižuje, po 15 letih pa se ne plača. Splošnega letnega davka na vrednost nepremičnin v Sloveniji ni.',
        sections: [
          {
            h2: 'Pregled: kateri davki se pojavijo pri nepremičnini?',
            blocks: [
              {
                table: {
                  head: ['Davek ali dajatev', 'Kdaj', 'Kdo plača', 'Višina'],
                  rows: [
                    ['Davek na promet nepremičnin', 'Prodaja, pri kateri se ne obračuna DDV', 'Prodajalec (kupec ga lahko prevzame s pogodbo)', '2 % prodajne cene'],
                    ['DDV', 'Prodaja novogradnje s strani davčnega zavezanca', 'Kupec v ceni', '9,5 % ali 22 %'],
                    ['Dohodnina od dobička iz kapitala', 'Prodaja ali podaritev nepremičnine', 'Prodajalec ali darovalec (fizična oseba)', '25 % → 0 % po 15 letih'],
                    ['Davek na dediščine in darila', 'Dedovanje ali darilo', 'Dedič ali obdarjenec', 'Prvi dedni red oproščen'],
                    ['NUSZ', 'Vsako leto', 'Neposredni uporabnik', 'Določi občina'],
                    ['Davek od premoženja', 'Vsako leto', 'Lastnik ali uživalec (fizična oseba)', 'Progresivna lestvica'],
                  ],
                },
              },
              {
                callout: {
                  title: 'Ni davčni nasvet',
                  text: 'Vodnik povzema pravila, kot jih opisuje Finančna uprava RS (FURS), in ni davčni ali pravni nasvet. Davčni predpisi se spreminjajo; za svoj primer preverite pri FURS, notarju ali davčnem svetovalcu.',
                },
              },
            ],
          },
          {
            h2: 'Davek na promet nepremičnin (2 %)',
            blocks: [
              {
                p: 'Davek se plača po stopnji 2 % pri prodaji nepremičnine, od katere ni bil obračunan DDV. Za promet se štejejo tudi zamenjava nepremičnin, odplačna ustanovitev stavbne pravice, finančni najem in priposestvovanje.',
              },
              {
                ul: [
                  'Zavezanec je prodajalec. Kupec lahko plačilo s pogodbo prevzame, a če davka ne plača, FURS izterja prodajalca.',
                  'Prodajalec mora vložiti napoved v 15 dneh po sklenitvi pogodbe pri finančnem uradu, na območju katerega leži nepremičnina, in priložiti pogodbo.',
                  'Davčna osnova je prodajna cena. Če ta ne ustreza ceni, ki bi se dala doseči na trgu, osnovo ugotovi davčni organ; zavezanec lahko predloži cenitev posamične tržne vrednosti.',
                  'Davek se odmeri z odločbo in plača v 30 dneh od njene vročitve.',
                  'Brez potrdila FURS o plačilu (ali o tem, da davka ni treba plačati) notar ne overi podpisov na pogodbi, zato vpis lastništva v zemljiško knjigo ni mogoč.',
                ],
              },
              {
                p: 'Davka ni treba plačati med drugim pri razdelitvi premoženja med zakoncema ali zunajzakonskima partnerjema, pri razlastitvi, pri razdrtju pogodbe in pri vložku nepremičnine v kapital družbe.',
              },
            ],
          },
          {
            h2: 'DDV pri nakupu novogradnje',
            blocks: [
              {
                p: 'Dobava objektov in zemljišč je oproščena DDV, razen dobave novogradenj oziroma novo vseljenih zgradb. Ko novogradnjo proda davčni zavezanec za DDV, se torej obračuna DDV, davek na promet nepremičnin pa se ne plača.',
              },
              {
                table: {
                  head: ['Stopnja DDV', 'Kdaj'],
                  rows: [
                    ['9,5 %', 'Stanovanja za trajno bivanje kot del socialne politike: v večstanovanjski stavbi do 120 m², v enostanovanjski stavbi do 250 m² uporabne površine'],
                    ['22 %', 'Večja stanovanja in hiše ter drugi objekti'],
                  ],
                },
              },
            ],
          },
          {
            h2: 'Davek ob prodaji: dobiček iz kapitala',
            blocks: [
              {
                p: 'Ko nepremičnino proda ali podari fizična oseba, se ugotavlja dobiček iz kapitala — razlika med vrednostjo ob odsvojitvi in vrednostjo ob pridobitvi. Vrednost ob pridobitvi se poveča za vlaganja, ki so povečala uporabno vrednost, ter za davek na promet oziroma davek na dediščine in darila, plačan ob pridobitvi; vrednost ob odsvojitvi se zmanjša za plačani davek na promet.',
              },
              {
                table: {
                  head: ['Obdobje lastništva', 'Stopnja'],
                  rows: [
                    ['manj kot 5 let', '25 %'],
                    ['po 5 letih', '20 %'],
                    ['po 10 letih', '15 %'],
                    ['po 15 letih', 'davka ni'],
                  ],
                },
              },
              {
                ul: [
                  'Dohodnine ni pri prodaji stanovanja ali stanovanjske hiše (z največ dvema stanovanjema), kjer je imel prodajalec prijavljeno stalno prebivališče, jo imel v lasti in v njej dejansko bival vsaj zadnja tri leta pred prodajo.',
                  'Pri podaritvi zakoncu ali otroku se lahko ugotavljanje davka odloži do naslednje obdavčljive odsvojitve.',
                  'Napoved je treba vložiti v 15 dneh od odsvojitve nepremičnine.',
                ],
              },
            ],
          },
          {
            h2: 'Letne dajatve lastnika: NUSZ in davek od premoženja',
            blocks: [
              { h3: 'Nadomestilo za uporabo stavbnega zemljišča (NUSZ)' },
              {
                p: 'Območja, merila za višino in oprostitve določi vsaka občina z odlokom, odmero pa za občino opravi FURS do konca marca za tekoče leto. Zavezanec je neposredni uporabnik — lastnik ali najemnik —, zato ob nakupu ali prodaji spremembo sporočite tudi občini. Kupec novega stanovanja je lahko na zahtevo pet let oproščen plačila, če je stroške urejanja stavbnega zemljišča plačal v ceni.',
              },
              { h3: 'Davek od premoženja' },
              {
                p: 'Plačujejo ga fizične osebe, ki imajo v lasti ali uživanju stavbe, dele stavb, stanovanja, garaže ali prostore za počitek in rekreacijo (npr. vikend), ne glede na to, ali jih uporabljajo same ali oddajajo. Napoved je treba vložiti v 15 dneh od pridobitve. Osnova se zniža za vrednost 160 m² stanovanjske površine, če je lastnik ali ožji družinski član v preteklem letu v njej stalno bival. Prvi lastniki novih stanovanj, hiš in garaž so na zahtevo deset let oproščeni.',
              },
            ],
          },
          {
            h2: 'Ali v Sloveniji obstaja davek na nepremičnine?',
            blocks: [
              {
                p: 'Splošnega letnega davka na vrednost nepremičnin trenutno ni. Zakon o davku na nepremičnine iz leta 2013 je Ustavno sodišče 21. 3. 2014 v celoti razveljavilo (U-I-313/13); do drugačne ureditve se uporabljajo prejšnji predpisi — zato se še vedno plačujeta NUSZ in davek od premoženja.',
              },
              {
                p: 'Ministrstvo za finance je decembra 2024 objavilo predlog izhodišč za obdavčitev premoženja. FURS-ova brošura o obdavčitvi nepremičnin (8. izdaja, september 2026) novega davka na nepremičnine ne navaja.',
              },
            ],
          },
          {
            h2: 'Dediščina, darilo in oddajanje v najem',
            blocks: [
              {
                p: 'Davka na dediščine in darila so oproščeni dediči prvega dednega reda (potomci in zakonec oziroma zunajzakonski partner) in z njimi izenačeni. Drugi plačajo davek po progresivni lestvici glede na dedni red: od 5 % do 14 % v drugem, od 8 % do 17 % v tretjem in od 12 % do 39 % za vse druge. Osnova za nepremičnino je njena primerljiva tržna vrednost; obdarjenec mora napoved vložiti v 15 dneh od sklenitve darilne pogodbe.',
              },
              {
                p: 'Dohodek iz oddajanja nepremičnine v najem se od 1. 1. 2023 obdavči po stopnji 25 %; osnova se zniža za normirane stroške v višini 10 % ali za dejanske stroške vzdrževanja.',
              },
            ],
          },
          {
            h2: 'Viri',
            blocks: [
              {
                sources: [
                  { label: 'FURS — Brošura o obdavčitvi nepremičnin (8. izdaja, september 2026)', href: 'https://www.fu.gov.si/fileadmin/Internet/Davki_in_druge_dajatve/Podrocja/Davek_na_promet_nepremicnin/Opis/Brosura_o_obdavcitvi_nepremicnin.docx' },
                  { label: 'FURS — davek na promet nepremičnin', href: 'https://www.fu.gov.si/davki_in_druge_dajatve/podrocja/davek_na_promet_nepremicnin/' },
                  { label: 'FURS — Obdavčitev z davkom na promet nepremičnin (podrobnejši opis)', href: 'https://www.fu.gov.si/fileadmin/Internet/Davki_in_druge_dajatve/Podrocja/Davek_na_promet_nepremicnin/Opis/Obdavcitev_z_davkom_na_promet_nepremicnin.docx' },
                  { label: 'FURS — Stopnje DDV (14. izdaja, junij 2026)', href: 'https://www.fu.gov.si/fileadmin/Internet/Davki_in_druge_dajatve/Podrocja/Davek_na_dodano_vrednost/Opis/Stopnje_DDV.docx' },
                  { label: 'FURS — Splošno o DDV (oprostitve pri dobavi objektov)', href: 'https://www.fu.gov.si/fileadmin/Internet/Davki_in_druge_dajatve/Podrocja/Davek_na_dodano_vrednost/Opis/Splosno_o_DDV.doc' },
                  { label: 'eDavki — napoved dohodnine od dobička pri odsvojitvi nepremičnine', href: 'https://edavki.durs.si/EdavkiPortal/OpenPortal/CommonPages/Opdynp/PageD.aspx?category=dohodnina_od_dobicka_iz_kapitala_od_odsvojitve_nepremicnin_kdnep_preb' },
                  { label: 'FURS — dohodek iz oddajanja premoženja v najem', href: 'https://www.fu.gov.si/fileadmin/Internet/Davki_in_druge_dajatve/Podrocja/Dohodnina/Dohodek_iz_oddajanja_premozenja_in_iz_premozenjske_pravice/Opis/Dohodek_iz_oddajanja_premozenja_v_najem.doc' },
                  { label: 'FURS — nadomestilo za uporabo stavbnega zemljišča (NUSZ)', href: 'https://www.fu.gov.si/davki_in_druge_dajatve/podrocja/nadomestilo_za_uporabo_stavbnega_zemljisca_nusz/' },
                  { label: 'FURS — davek od premoženja', href: 'https://www.fu.gov.si/davki_in_druge_dajatve/podrocja/davek_od_premozenja/' },
                  { label: 'Ustavno sodišče RS — odločba U-I-313/13 (Zakon o davku na nepremičnine)', href: 'https://www.us-rs.si/sl/zadeve-in-odlocitve/odlocitve/u-i-31313' },
                  { label: 'Ministrstvo za finance — predlog izhodišč za obdavčitev premoženja (december 2024)', href: 'https://www.gov.si/assets/ministrstva/MF/Glavno-urednistvo/Novice/DOKUMENTI/Davek-na-nepremicnine/Predlog-izhodisc-za-obdavcitev-premozenja-in-razbremenitev-dela.pdf' },
                ],
              },
            ],
          },
        ],
        faq: [
          {
            question: 'Kdo plača davek na promet nepremičnin, kupec ali prodajalec?',
            answer:
              'Po zakonu prodajalec. Kupec lahko plačilo s pogodbo prevzame, vendar zavezanec ostane prodajalec: če kupec davka ne plača, FURS izterja prodajalca.',
          },
          {
            question: 'Koliko davka plačam, če stanovanje prodam po desetih letih?',
            answer:
              'Stopnja za dobiček iz kapitala je po desetih letih lastništva 15 %, po 15 letih pa davka ni. Če ste v stanovanju imeli stalno prebivališče in v njem dejansko živeli vsaj zadnja tri leta pred prodajo, dohodnine od dobička ne plačate. Davek na promet (2 %) je ločen davek.',
          },
          {
            question: 'Ali pri nakupu novogradnje plačam davek na promet nepremičnin?',
            answer:
              'Ne, kadar prodajalec obračuna DDV. Takrat je v ceni DDV — 9,5 % za stanovanja do 120 m² v večstanovanjski stavbi oziroma hiše do 250 m² uporabne površine, sicer 22 %.',
          },
          {
            question: 'Ali v Sloveniji plačujemo letni davek na nepremičnine?',
            answer:
              'Splošnega davka na vrednost nepremičnin ni. Letno se plačujeta nadomestilo za uporabo stavbnega zemljišča (NUSZ), ki ga določi občina, in davek od premoženja.',
          },
        ],
      },
      en: {
        path: '/en/guide/property-taxes-slovenia',
        title: 'Property Taxes in Slovenia (2026) | nReport',
        description:
          'Which taxes apply when you buy, own and sell property in Slovenia: the 2 % transfer tax, VAT on new builds, capital gains tax, the NUSZ charge and property tax.',
        breadcrumb: 'Property taxes',
        eyebrow: 'Taxes',
        h1: 'Property Taxes in Slovenia',
        intro:
          'When you buy a resale property in Slovenia, a 2 % real estate transfer tax is due, owed by law by the seller; when the seller charges VAT instead — typically on new builds — no transfer tax is due. As an owner you then pay the annual building land use charge (NUSZ) and the tax on buildings, and when you sell, any gain can be taxed: at 25 %, falling with each five years of ownership, and not at all after 15 years. Slovenia has no general annual tax on property value.',
        sections: [
          {
            h2: 'Overview: which taxes apply to property?',
            blocks: [
              {
                table: {
                  head: ['Tax or charge', 'When', 'Who pays', 'Amount'],
                  rows: [
                    ['Real estate transfer tax', 'A sale on which no VAT is charged', 'Seller (the buyer may take it over by contract)', '2 % of the sale price'],
                    ['VAT', 'Sale of a new build by a VAT-registered seller', 'Buyer, in the price', '9.5 % or 22 %'],
                    ['Capital gains tax', 'Sale or gift of property', 'Seller or donor (individual)', '25 % → 0 % after 15 years'],
                    ['Inheritance and gift tax', 'Inheritance or gift', 'Heir or recipient', 'First-order heirs exempt'],
                    ['NUSZ', 'Every year', 'Direct user', 'Set by the municipality'],
                    ['Tax on buildings (davek od premoženja)', 'Every year', 'Owner or holder (individual)', 'Progressive scale'],
                  ],
                },
              },
              {
                callout: {
                  title: 'Not tax advice',
                  text: 'This guide summarises the rules as described by the Financial Administration of Slovenia (FURS) and is not tax or legal advice. Tax rules change; for your case, check with FURS, a notary or a tax adviser.',
                },
              },
            ],
          },
          {
            h2: 'Real estate transfer tax (2 %)',
            blocks: [
              {
                p: 'The tax is charged at 2 % on the sale of property on which no VAT was charged. Exchanges of property, the paid creation of a building right, financial leasing and acquisition by adverse possession also count as transfers.',
              },
              {
                ul: [
                  'The taxpayer is the seller. The buyer may take over payment in the contract, but if the buyer does not pay, FURS collects from the seller.',
                  'The seller must file a return within 15 days of concluding the contract at the tax office for the area where the property lies, with the contract attached.',
                  'The tax base is the sale price. If it does not match the price achievable on the market, the tax authority determines the base; the taxpayer may submit an individual market-value appraisal.',
                  'The tax is assessed by decision and must be paid within 30 days of its service.',
                  'Without FURS confirmation that the tax was paid (or is not due), a notary will not certify the signatures on the contract, so ownership cannot be registered in the land register.',
                ],
              },
              {
                p: 'No tax is due, among other cases, when property is divided between spouses or unmarried partners, on expropriation, when a transfer contract is rescinded, or when property is contributed as capital to a company.',
              },
            ],
          },
          {
            h2: 'VAT when buying a new build',
            blocks: [
              {
                p: 'Supplies of buildings and land are exempt from VAT, except supplies of new builds or newly occupied buildings. So when a VAT-registered seller sells a new build, VAT is charged and no transfer tax is due.',
              },
              {
                table: {
                  head: ['VAT rate', 'When'],
                  rows: [
                    ['9.5 %', 'Dwellings for permanent residence as part of social policy: up to 120 m² in a multi-dwelling building, up to 250 m² of usable area in a single-dwelling building'],
                    ['22 %', 'Larger apartments and houses and other buildings'],
                  ],
                },
              },
            ],
          },
          {
            h2: 'Tax when selling: capital gains',
            blocks: [
              {
                p: 'When an individual sells or gives away property, a capital gain is determined — the difference between the value at disposal and at acquisition. The acquisition value is increased by investments that raised the property’s usable value and by transfer tax or inheritance and gift tax paid on acquisition; the disposal value is reduced by the transfer tax paid on the sale.',
              },
              {
                table: {
                  head: ['Holding period', 'Rate'],
                  rows: [
                    ['under 5 years', '25 %'],
                    ['after 5 years', '20 %'],
                    ['after 10 years', '15 %'],
                    ['after 15 years', 'no tax'],
                  ],
                },
              },
              {
                ul: [
                  'No tax is due on the sale of an apartment or house (with at most two dwellings) where the seller had registered permanent residence, owned it and actually lived for at least the last three years before the sale.',
                  'For a gift to a spouse or child, the tax assessment can be deferred until the next taxable disposal.',
                  'The return must be filed within 15 days of disposing of the property.',
                ],
              },
            ],
          },
          {
            h2: 'Annual charges for owners: NUSZ and the tax on buildings',
            blocks: [
              { h3: 'Building land use charge (NUSZ)' },
              {
                p: 'Each municipality sets the areas, rate criteria and exemptions by ordinance, and FURS assesses the charge on its behalf by the end of March for the current year. The payer is the direct user — the owner or a tenant — so notify the municipality as well when you buy or sell. A buyer of a new apartment can, on request, be exempt for five years if the cost of servicing the building land was paid in the price.',
              },
              { h3: 'Tax on buildings (davek od premoženja)' },
              {
                p: 'Paid by individuals who own or hold buildings, building parts, apartments, garages or holiday premises, whether they use them themselves or let them. A return is due within 15 days of acquisition. The base is reduced by the value of 160 m² of living space if the owner or a close family member lived there permanently in the previous year. First owners of new apartments, houses and garages can, on request, be exempt for ten years.',
              },
            ],
          },
          {
            h2: 'Is there a property tax in Slovenia?',
            blocks: [
              {
                p: 'There is currently no general annual tax on property value. The 2013 Real Property Tax Act was annulled in full by the Constitutional Court on 21 March 2014 (U-I-313/13); until new rules are adopted, the previous regulations apply — which is why NUSZ and the tax on buildings are still paid.',
              },
              {
                p: 'In December 2024 the Ministry of Finance published proposed starting points for taxing property. The FURS brochure on property taxation (8th edition, September 2026) lists no new property tax.',
              },
            ],
          },
          {
            h2: 'Inheritance, gifts and rental income',
            blocks: [
              {
                p: 'First-order heirs (descendants and the spouse or unmarried partner) and those treated as equal to them are exempt from inheritance and gift tax. Others pay on a progressive scale by order of inheritance: 5 % to 14 % in the second order, 8 % to 17 % in the third and 12 % to 39 % for everyone else. The base for property is its comparable market value; a gift recipient must file a return within 15 days of the gift contract.',
              },
              {
                p: 'Since 1 January 2023, income from letting property is taxed at 25 %; the base is reduced by standardised costs of 10 % or by actual maintenance costs.',
              },
            ],
          },
          {
            h2: 'Sources',
            blocks: [
              {
                sources: [
                  { label: 'FURS — brochure on property taxation (8th edition, September 2026, in Slovenian)', href: 'https://www.fu.gov.si/fileadmin/Internet/Davki_in_druge_dajatve/Podrocja/Davek_na_promet_nepremicnin/Opis/Brosura_o_obdavcitvi_nepremicnin.docx' },
                  { label: 'FURS — real estate transfer tax (in Slovenian)', href: 'https://www.fu.gov.si/davki_in_druge_dajatve/podrocja/davek_na_promet_nepremicnin/' },
                  { label: 'FURS — detailed description of the transfer tax (in Slovenian)', href: 'https://www.fu.gov.si/fileadmin/Internet/Davki_in_druge_dajatve/Podrocja/Davek_na_promet_nepremicnin/Opis/Obdavcitev_z_davkom_na_promet_nepremicnin.docx' },
                  { label: 'FURS — VAT rates (14th edition, June 2026, in Slovenian)', href: 'https://www.fu.gov.si/fileadmin/Internet/Davki_in_druge_dajatve/Podrocja/Davek_na_dodano_vrednost/Opis/Stopnje_DDV.docx' },
                  { label: 'FURS — VAT overview, exemptions for buildings (in Slovenian)', href: 'https://www.fu.gov.si/fileadmin/Internet/Davki_in_druge_dajatve/Podrocja/Davek_na_dodano_vrednost/Opis/Splosno_o_DDV.doc' },
                  { label: 'eDavki — capital gains return on disposal of property (in Slovenian)', href: 'https://edavki.durs.si/EdavkiPortal/OpenPortal/CommonPages/Opdynp/PageD.aspx?category=dohodnina_od_dobicka_iz_kapitala_od_odsvojitve_nepremicnin_kdnep_preb' },
                  { label: 'FURS — income from letting property (in Slovenian)', href: 'https://www.fu.gov.si/fileadmin/Internet/Davki_in_druge_dajatve/Podrocja/Dohodnina/Dohodek_iz_oddajanja_premozenja_in_iz_premozenjske_pravice/Opis/Dohodek_iz_oddajanja_premozenja_v_najem.doc' },
                  { label: 'FURS — building land use charge, NUSZ (in Slovenian)', href: 'https://www.fu.gov.si/davki_in_druge_dajatve/podrocja/nadomestilo_za_uporabo_stavbnega_zemljisca_nusz/' },
                  { label: 'FURS — tax on buildings (in Slovenian)', href: 'https://www.fu.gov.si/davki_in_druge_dajatve/podrocja/davek_od_premozenja/' },
                  { label: 'Constitutional Court — decision U-I-313/13 (in Slovenian)', href: 'https://www.us-rs.si/sl/zadeve-in-odlocitve/odlocitve/u-i-31313' },
                  { label: 'Ministry of Finance — proposed starting points for property taxation, December 2024 (in Slovenian)', href: 'https://www.gov.si/assets/ministrstva/MF/Glavno-urednistvo/Novice/DOKUMENTI/Davek-na-nepremicnine/Predlog-izhodisc-za-obdavcitev-premozenja-in-razbremenitev-dela.pdf' },
                ],
              },
            ],
          },
        ],
        faq: [
          {
            question: 'Who pays the real estate transfer tax, the buyer or the seller?',
            answer:
              'By law, the seller. The buyer can take over payment in the contract, but the seller remains the taxpayer: if the buyer does not pay, FURS collects from the seller.',
          },
          {
            question: 'How much tax do I pay if I sell an apartment after ten years?',
            answer:
              'The capital gains rate after ten years of ownership is 15 %, and after 15 years no tax is due. If you had registered permanent residence and actually lived in the apartment for at least the last three years before the sale, no capital gains tax is due. The 2 % transfer tax is a separate tax.',
          },
          {
            question: 'Do I pay transfer tax when buying a new build?',
            answer:
              'Not when the seller charges VAT. The price then includes VAT — 9.5 % for apartments up to 120 m² in a multi-dwelling building or houses up to 250 m² of usable area, otherwise 22 %.',
          },
          {
            question: 'Is there an annual property tax in Slovenia?',
            answer:
              'There is no general tax on property value. Owners pay the building land use charge (NUSZ), set by the municipality, and the tax on buildings every year.',
          },
        ],
      },
    },
  },
];

// --------------------------------------------------------------------- helpers

/**
 * Stable in-page anchor for a glossary term. Shared by the React renderer (the
 * `<dt id>`) and the build layer (the DefinedTerm `@id`/`url`), so the JSON-LD
 * always points at an anchor that exists on the page.
 * @param {string} term
 */
export function termAnchor(term) {
  return (
    'pojem-' +
    term
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
  );
}

/**
 * Every glossary term in a guide locale's `terms` blocks, in page order. Guides
 * with terms get a DefinedTermSet in their JSON-LD (scripts/seo-config.js).
 * @param {{ sections: { blocks: GuideBlock[] }[] }} content
 * @returns {GuideTerm[]}
 */
export function guideTerms(content) {
  /** @type {GuideTerm[]} */
  const out = [];
  for (const section of content.sections ?? []) {
    for (const block of section.blocks) {
      if ('terms' in block) out.push(...block.terms);
    }
  }
  return out;
}

/** Every guide + hub locale entry, flattened. @returns {{ path: string, locale: 'sl'|'en'|'de'|'ru', group: string, isHub: boolean }[]} */
export function guideIndex() {
  /** @type {{ path: string, locale: any, group: string, isHub: boolean }[]} */
  const out = [];
  for (const [locale, entry] of Object.entries(GUIDE_HUB.locales)) {
    out.push({ path: entry.path, locale, group: 'hub', isHub: true });
  }
  for (const g of GUIDES) {
    for (const [locale, entry] of Object.entries(g.locales)) {
      out.push({ path: entry.path, locale, group: g.group, isHub: false });
    }
  }
  return out;
}

/** All guide + hub paths (for prerender + client hydration). */
export function allGuidePaths() {
  return guideIndex().map((e) => e.path);
}

/** Locale of a guide/hub path, or null if not a guide path. */
export function guideLocaleForPath(path) {
  const hit = guideIndex().find((e) => e.path === path);
  return hit ? hit.locale : null;
}

/** Full data for a guide/hub path: content + group + locale + kind. */
export function getGuideByPath(path) {
  for (const [locale, entry] of Object.entries(GUIDE_HUB.locales)) {
    if (entry.path === path) {
      return { kind: 'hub', locale, group: 'hub', content: entry, meta: null };
    }
  }
  for (const g of GUIDES) {
    for (const [locale, entry] of Object.entries(g.locales)) {
      if (entry.path === path) {
        return {
          kind: 'guide',
          locale,
          group: g.group,
          content: entry,
          meta: {
            datePublished: g.datePublished,
            dateModified: g.dateModified,
            changefreq: g.changefreq,
            priority: g.priority,
          },
        };
      }
    }
  }
  return null;
}

/** hreflang alternates for a group (only locales that exist; x-default = sl or first). */
export function guideHreflang(group) {
  const entry = group === 'hub' ? GUIDE_HUB : GUIDES.find((g) => g.group === group);
  if (!entry) return [];
  const locales = Object.entries(entry.locales);
  const alternates = locales.map(([locale, e]) => ({
    hreflang: locale,
    href: `${SITE_URL}${e.path}`,
  }));
  const def = entry.locales.sl ?? locales[0][1];
  alternates.push({ hreflang: 'x-default', href: `${SITE_URL}${def.path}` });
  return alternates;
}

/** Cards for the hub listing in a given locale. */
export function hubListing(locale) {
  return GUIDES.map((g) => g.locales[locale])
    .filter(Boolean)
    .map((e) => ({ path: e.path, title: e.breadcrumb, description: e.description }));
}

/** Related guides in a locale, excluding the current group. */
export function relatedGuides(locale, excludeGroup) {
  return GUIDES.filter((g) => g.group !== excludeGroup)
    .map((g) => g.locales[locale])
    .filter(Boolean)
    .map((e) => ({ path: e.path, title: e.breadcrumb }));
}

/** Group id ('hub' or a guide group) for a path, or null. */
export function guideGroupForPath(path) {
  const hit = guideIndex().find((e) => e.path === path);
  return hit ? hit.group : null;
}

/** Path for a given group + locale, or null if that translation does not exist. */
export function guidePathForGroup(group, locale) {
  const entry = group === 'hub' ? GUIDE_HUB : GUIDES.find((g) => g.group === group);
  return entry?.locales?.[locale]?.path ?? null;
}

/** Sitemap rows for all guide + hub locale entries. */
export function guideSitemapEntries() {
  /** @type {{ loc: string, changefreq: string, priority: string }[]} */
  const rows = [];
  for (const entry of Object.values(GUIDE_HUB.locales)) {
    rows.push({ loc: `${SITE_URL}${entry.path}`, changefreq: 'weekly', priority: '0.7' });
  }
  for (const g of GUIDES) {
    for (const entry of Object.values(g.locales)) {
      rows.push({ loc: `${SITE_URL}${entry.path}`, changefreq: g.changefreq, priority: g.priority });
    }
  }
  return rows;
}
